import { connect, useDispatch, useSelector, Provider } from 'react-redux';
import { bindActionCreators, compose, combineReducers } from 'redux';
import { cmsSelectors, cmsReducers, authSelectors, authReducers, longFlowSelectors, queryParamsSelectors, settingsReducers, queryParamsReducers, settingsSelectors, longFlowReducers, commonReducers, persist } from '@zillow/zhl-redux-commons';
import { useEffect, useCallback, createContext, Component, useState, useMemo, useContext } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';
import { typographyMixin, spaceMixin, AnchorMixin, TableMixin, token, ModalDialog, Heading, Anchor, mediaMixin, Text, UnstyledButton, IconChevronUp, IconChevronDown, DetailedIconPhone, Spacer, TextButton, IconMenu, IconClose, Icon, VisuallyHiddenMixin, Flex, FONT_TYPES, IconPhoneOutline, IconBuildingsOutline, Paragraph, IconAskQuestionOutline, Spinner, ThemeConstellation, StyledButton, Button, Divider } from '@zillow/constellation';
import { trackEvent } from '@zillow/universal-analytics';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import showdown from 'showdown';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { constants } from '@zillow/zhl-yup';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _inheritsLoose from '@babel/runtime/helpers/esm/inheritsLoose';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import intersection from 'lodash/intersection';
import createStore from '@zillow/standard-redux';
import sessionStorage from 'redux-persist/lib/storage/session';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

var _excluded = ["smallFontSizes"],
    _excluded2 = ["smallFontSizes"],
    _excluded3 = ["smallFontSizes"],
    _excluded4 = ["smallFontSizes"],
    _excluded5 = ["smallFontSizes"],
    _excluded6 = ["smallFontSizes"];

var _templateObject;
var converter = new showdown.Converter({
  tables: true,
  noHeaderId: true,
  headerLevelStart: 1,
  simpleLineBreaks: true,
  extensions: []
});
var SMALL_LOOKUP = {
  body: 'bodySmall',
  bodyHeading: 'bodySmallHeading',
  finePrint: 'legal',
  bodySmallHeading: 'finePrintHeading',
  bodySmall: 'finePrint'
};

var getFontType = function getFontType(smallFontSizes, targetFontType) {
  if (smallFontSizes) {
    return SMALL_LOOKUP[targetFontType];
  }

  return targetFontType;
};

var headings = [1, 2, 3, 4, 5, 6].reduce(function (acc, level) {
  return css(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n        ", "\n        h", " {\n            ", "\n        }\n    "])), acc, level, function (props) {
    return typographyMixin(Object.assign({}, props, {
      fontType: "h" + level
    }));
  });
}, '');
var StyledMarkdown = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1v05icw-0"
})(["", " ul{list-style-type:disc;}", " strong{", "}sub{", "}ol{list-style-type:decimal;ol{list-style-type:lower-alpha;margin-bottom:", ";}}ul,ol{padding-left:", ";padding-bottom:", ";}a{", "}p{margin-bottom:", ";", "}table{", ";}th{", ";}td{", "}", " &&&{td,th{padding:", "px ", "px;border:", "px solid ", ";}}"], headings, function (_ref) {
  var smallFontSizes = _ref.smallFontSizes,
      props = _objectWithoutProperties(_ref, _excluded);

  return typographyMixin(Object.assign({}, props, {
    fontType: getFontType(smallFontSizes, 'body')
  }));
}, function (_ref2) {
  var smallFontSizes = _ref2.smallFontSizes,
      props = _objectWithoutProperties(_ref2, _excluded2);

  return typographyMixin(Object.assign({}, props, {
    fontType: getFontType(smallFontSizes, 'bodyHeading')
  }));
}, function (_ref3) {
  var smallFontSizes = _ref3.smallFontSizes,
      props = _objectWithoutProperties(_ref3, _excluded3);

  return typographyMixin(Object.assign({}, props, {
    fontType: getFontType(smallFontSizes, 'fineprint')
  }));
}, spaceMixin('sm'), spaceMixin('lg'), spaceMixin('md'), AnchorMixin, spaceMixin('sm'), function (_ref4) {
  var smallFontSizes = _ref4.smallFontSizes,
      props = _objectWithoutProperties(_ref4, _excluded4);

  return typographyMixin(Object.assign({}, props, {
    fontType: getFontType(smallFontSizes, 'body')
  }));
}, function (props) {
  return TableMixin(Object.assign({}, props, {
    alignments: [],
    appearance: 'grid',
    size: 'md'
  }));
}, function (_ref5) {
  var smallFontSizes = _ref5.smallFontSizes,
      props = _objectWithoutProperties(_ref5, _excluded5);

  return typographyMixin(Object.assign({}, props, {
    fontType: getFontType(smallFontSizes, 'bodySmallHeading')
  }));
}, function (_ref6) {
  var smallFontSizes = _ref6.smallFontSizes,
      props = _objectWithoutProperties(_ref6, _excluded6);

  return typographyMixin(Object.assign({}, props, {
    fontType: getFontType(smallFontSizes, 'bodySmall')
  }));
},
/*
  Unfortunately this is necessary because the styles for cells are managed by the TableMixin and rely
  on the CSS selectors of the React components, which we cannot get in target via markdown.
*/
'', token('Table.verticalPadding.md'), token('Table.horizontalPadding.md'), token('Table.borderWidth'), token('Table.borderColor'));

var RenderMarkdown = function RenderMarkdown(_ref7) {
  var markdown = _ref7.markdown,
      smallFontSizes = _ref7.smallFontSizes;
  return /*#__PURE__*/jsx(StyledMarkdown, {
    smallFontSizes: smallFontSizes // eslint-disable-next-line react/no-danger
    ,
    dangerouslySetInnerHTML: {
      __html: converter.makeHtml(markdown)
    }
  });
};

RenderMarkdown.propTypes = {
  markdown: PropTypes.string,
  smallFontSizes: PropTypes.bool
};

var CenteredHeading = /*#__PURE__*/styled(Heading).withConfig({
  componentId: "sc-qyzw7h-0"
})(["text-align:center;"]);

var ABAD = function ABAD(_ref) {
  var fetchAbadContent = _ref.fetchAbadContent,
      shouldShowAbad = _ref.shouldShowAbad,
      dismissAbad = _ref.dismissAbad,
      children = _ref.children,
      abadContent = _ref.abadContent;
  useEffect(function () {
    var showAbadIfNecessary = function showAbadIfNecessary() {
      return new Promise(function ($return, $error) {
        if (shouldShowAbad) {
          trackEvent({
            category: 'Mortgages',
            action: 'ZHL ABAD displayed'
          });
          return Promise.resolve(fetchAbadContent()).then(function ($await_2) {
            try {
              return $If_1.call(this);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        }

        function $If_1() {
          return $return();
        }

        return $If_1.call(this);
      });
    };

    showAbadIfNecessary(); // Only want to fetch abad if applicable on component mount
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var closeAbad = useCallback(function () {
    trackEvent({
      category: 'Mortgages',
      action: 'ZHL ABAD dismissed'
    });
    dismissAbad();
  }, [dismissAbad]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [children, /*#__PURE__*/jsx(ModalDialog, {
      isOpen: shouldShowAbad,
      wide: true,
      shouldCloseOnESCKeyPress: false,
      shouldCloseOnOutsideClick: false,
      onClose: closeAbad,
      header: /*#__PURE__*/jsx(Heading, {
        level: 2,
        children: "Here's what you need to know about Zillow and its affiliated lender, Zillow Home Loans."
      }),
      body: /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(CenteredHeading, {
          level: "5",
          marginBottom: "sm",
          children: "Affiliated Business Arrangement Disclosure"
        }), /*#__PURE__*/jsx(RenderMarkdown, {
          markdown: abadContent
        })]
      })
    })]
  });
};

ABAD.propTypes = {
  children: PropTypes.node,
  shouldShowAbad: PropTypes.bool,
  dismissAbad: PropTypes.func,
  fetchAbadContent: PropTypes.func,
  abadContent: PropTypes.string
};

var ABAD$1 = connect(function (state) {
  return {
    shouldShowAbad: cmsSelectors.shouldShowAbadSelector(state),
    abadContent: cmsSelectors.abadContentSelector(state)
  };
}, function (dispatch) {
  return bindActionCreators({
    dismissAbad: cmsReducers.dismissAbad,
    fetchAbadContent: cmsReducers.fetchAbadContent
  }, dispatch);
})(ABAD);

var _excluded$1 = ["title"];

var SvgZhlLogo = function SvgZhlLogo(_ref) {
  var title = _ref.title,
      props = _objectWithoutProperties(_ref, _excluded$1);

  return /*#__PURE__*/jsxs("svg", Object.assign({
    viewBox: "0 0 320 35"
  }, props, {
    children: [title ? /*#__PURE__*/jsx("title", {
      children: title
    }) : null, /*#__PURE__*/jsx("path", {
      d: "M6.21 8.24L0 13.16v5.23l.38-.26c2.11-1.38 7.9-4.14 12.46-5.97 4.05-1.6 3.9-1.58 4.93-.46.46.5.96 1.09 1.14 1.3l.29.38-2.61 2.61c-3.17 3.17-5.46 5.92-4.54 5.5 2.54-1.15 8.11-3.12 11.31-4l2-.54.05-1.84.05-1.84-6.26-4.93c-3.44-2.72-6.37-4.96-6.53-4.98-.14-.01-3.04 2.18-6.46 4.88zm60.19 9.64v13.28h5.76V4.6H66.4v13.28zm9.76 0v13.28h5.76V4.6h-5.76v13.28zM58.56 5.27c-1.34.38-2.24 1.58-2.24 3.02 0 1.82 1.15 3.04 2.98 3.15 1.98.13 3.26-.98 3.39-2.91.11-1.63-.75-2.86-2.29-3.28-.75-.21-1.09-.21-1.84.02zm80.16 13.81v12.08h3.52v-8.8h12.16v8.8h3.52V7h-3.52v12.16h-12.16V7h-3.52v12.08zM34.24 9.72v2.56h4.88c2.69 0 4.88.03 4.88.08s-2.27 3.94-5.04 8.66c-4.78 8.13-5.04 8.59-5.04 9.34v.8h19.36v-5.28L48 25.85l-5.3-.05 5.04-8.56c4.69-7.97 5.06-8.61 5.06-9.33v-.75H34.24v2.56zm198.24 9.44v12h12.16v-3.2H236V7.16h-3.52v12zM132.05 9.88c-.7.46-1.07 1.3-.93 2.18.27 1.81 2.48 2.46 3.71 1.12.46-.5.53-.69.53-1.46 0-.78-.06-.96-.53-1.47-.46-.5-.66-.59-1.41-.64-.67-.05-.97.01-1.37.27zm1.79.38c.53.22 1.04.94 1.04 1.46s-.51 1.23-1.06 1.46c-1.47.62-2.82-1.23-1.74-2.45l.38-.45.02 1.31c0 1.07.05 1.33.24 1.33.13 0 .24-.14.24-.32s.06-.32.16-.32c.08 0 .22.14.32.32s.32.32.48.32c.35 0 .42-.16.14-.43-.13-.13-.13-.27 0-.5.1-.18.18-.54.18-.82 0-.56-.35-.82-1.15-.83-.43 0-.48-.03-.21-.14.18-.08.34-.14.37-.14.01-.02.29.07.59.2zm-41.2 3.14c-1.97.27-3.76 1.06-5.15 2.24-.98.83-2.08 2.53-2.53 3.84-.5 1.42-.61 3.71-.26 5.18.7 2.96 2.96 5.46 5.9 6.5 1.38.5 4.27.62 5.71.26 2.24-.58 4.43-2.11 5.6-3.94 1.66-2.59 1.86-6.27.45-9.12-.74-1.52-2.56-3.3-4.13-4.03-1.8-.87-3.78-1.19-5.59-.93zm3.09 5.4c1.33.72 2.03 2.02 2.03 3.78 0 3.07-2.93 4.99-5.58 3.68-3.36-1.68-2.69-7.22.96-7.89.96-.17 1.68-.05 2.59.43zm71.95-5.31c-3.22.69-5.76 3.23-6.64 6.62-.37 1.42-.27 4.18.21 5.5 1.04 2.9 3.34 5.02 6.21 5.78 1.18.3 3.68.3 4.99 0 3.12-.74 5.68-3.33 6.51-6.56.3-1.2.3-3.38 0-4.7-1.15-4.89-6.05-7.77-11.28-6.64zm4.83 3.55c4.29 2.11 4.3 8.7.02 10.88-2.75 1.41-6.29.21-7.63-2.58-1.65-3.41-.05-7.6 3.31-8.69.4-.13 1.22-.21 1.95-.18 1.09.05 1.46.13 2.35.57zm16.34-3.58c-1.15.37-2.53 1.44-3.06 2.42-.14.26-.18.06-.19-.93v-1.23h-3.52v17.44h3.5l.05-5.65c.06-6.34.1-6.53 1.26-7.84.75-.86 1.68-1.28 2.88-1.26 1.55 0 2.51.7 3.14 2.3.24.59.29 1.52.34 6.56l.05 5.89h3.48l.05-5.65c.06-6.29.1-6.5 1.2-7.78.78-.93 1.65-1.34 2.85-1.34 1.79 0 3.02 1.06 3.42 2.94.11.48.18 3.02.18 6.3v5.52h3.54l-.05-6.51c-.08-7.25-.11-7.52-1.17-9.09-1.84-2.7-6.58-3.12-9.15-.8-.43.38-.93.94-1.09 1.26-.37.67-.5.72-.62.19-.13-.53-1.47-1.98-2.16-2.32-1.25-.63-3.6-.84-4.93-.42zm28.51-.06c-4.19.85-6.64 4.21-6.64 9.12 0 3.02.74 5.06 2.48 6.8 1.6 1.6 3.49 2.32 6.11 2.32 1.79 0 2.86-.21 4.14-.82 1.79-.83 3.17-2.37 3.68-4.06l.21-.72h-1.81c-1.74 0-1.81.02-1.98.38-.3.69-1.22 1.58-1.9 1.89-1.55.69-3.92.48-5.34-.46-1.07-.7-1.82-2.06-2.13-3.86l-.08-.51h13.22l.11-1.14c.19-1.9-.19-3.81-1.09-5.33-.61-1.02-2.21-2.46-3.34-2.98-1.64-.73-3.88-.99-5.64-.63zm4.18 3.12c1.42.7 2.46 2.26 2.46 3.71v.53h-9.81l.1-.78c.22-1.87 1.62-3.36 3.58-3.86 1.01-.26 2.71-.07 3.67.4zm30.84-3.04c-3.23.7-5.78 3.22-6.62 6.58-1.3 5.02 1.55 10.08 6.35 11.33 1.25.34 3.86.34 5.07.02 3.09-.8 5.62-3.33 6.42-6.42.3-1.18.3-3.86 0-5.02-.86-3.28-3.49-5.79-6.77-6.48-1.31-.3-3.15-.3-4.45-.01zm4.79 3.52c2.27 1.12 3.63 3.86 3.2 6.4-.38 2.21-1.49 3.73-3.36 4.59-.9.43-1.23.5-2.37.5-1.06 0-1.47-.08-2.21-.42a5.893 5.893 0 0 1-2.86-2.8c-.69-1.41-.82-3.33-.3-4.85.45-1.34 1.34-2.5 2.42-3.15 1.22-.72 1.65-.83 3.2-.77 1.11.03 1.51.11 2.28.5zm15.69-3.52c-3.1.67-5.76 3.38-6.54 6.66-1.22 5.18 1.78 10.29 6.67 11.34.54.13 1.54.18 2.51.13 1.46-.06 1.73-.14 2.8-.67.82-.4 1.44-.85 2-1.47l.82-.9v2.6h3.36V13.73h-3.36v2.77l-.46-.64c-1.52-2.11-4.68-3.07-7.8-2.38zm4.45 3.72c.42.18 1.1.66 1.54 1.06 3.02 2.83 2.24 8.13-1.46 9.79-3.15 1.41-6.59-.14-7.63-3.44-.99-3.17.51-6.46 3.49-7.58 1.02-.39 3.02-.31 4.06.17zm17.92-3.8c-1.57.34-3.02 1.25-3.89 2.46l-.46.62v-2.77h-3.36v17.44h3.36v-4.37c0-2.38.08-4.88.16-5.54.4-3.01 2.22-4.86 4.77-4.85 2.43.02 3.79 1.36 4.19 4.14.08.59.16 3.23.16 5.84v4.77h3.36v-6.21c0-5.06-.05-6.38-.24-7.15-.51-1.98-1.98-3.65-3.68-4.18-.96-.28-3.41-.41-4.37-.2zm15.68.08c-2.9.64-4.38 2.27-4.4 4.85-.02 1.52.35 2.42 1.31 3.28.83.75 1.44 1.01 4.46 1.97 3.78 1.2 4.46 1.66 4.4 3.06-.05 1.46-1.23 2.21-3.41 2.21-.94-.02-1.39-.1-2.1-.43-1.07-.48-1.54-1.09-1.79-2.29l-.18-.88h-3.34l.1.98c.11 1.31.86 2.82 1.84 3.68 1.49 1.33 3.31 1.84 6.06 1.7 2.61-.11 4.26-.83 5.31-2.3.64-.86.82-1.52.82-2.98 0-1.07-.06-1.39-.43-2.05-.74-1.36-1.52-1.81-5.47-3.09-3.76-1.22-4.18-1.49-4.18-2.77 0-1.33 1.04-2.14 2.9-2.26.83-.05 1.22.02 1.89.29.98.43 1.38.94 1.65 2.06l.18.82h3.36l-.11-.85c-.24-1.76-1.41-3.49-2.94-4.32-1.59-.88-3.86-1.13-5.93-.68zM56.64 22.44v8.72h5.76V13.72h-5.76v8.72zm46.91-8.42c.03.18 1.28 4.1 2.77 8.72l2.72 8.42h5.28l1.15-3.63c.62-2.02 1.26-4.08 1.42-4.59l.26-.94.7 2.38c.38 1.31 1.01 3.36 1.38 4.54l.7 2.16 2.58.05 2.59.03 1.2-3.71c.66-2.05 1.92-5.97 2.8-8.69l1.58-4.96-2.58-.05c-1.41-.02-2.62.02-2.69.08-.11.13-2.4 7.58-2.72 8.88-.1.43-.22.77-.29.77-.05 0-.3-.74-.54-1.65-.24-.9-.9-3.07-1.46-4.83l-1.01-3.2h-4.26l-.62 1.92c-.34 1.06-1.01 3.25-1.5 4.86-.48 1.63-.9 2.99-.93 3.02-.03.03-.11-.19-.18-.48-.13-.54-1.3-4.53-2.26-7.62l-.56-1.79h-2.8c-2.65.01-2.78.02-2.73.31z"
    }), /*#__PURE__*/jsx("path", {
      d: "M9.36 17.28c-1.23.56-3.84 1.82-5.81 2.82L0 21.91v9.24h25.44v-4.88c0-3.79-.05-4.88-.19-4.88-.11 0-.9.18-1.73.4-3.84 1.04-11.18 3.78-14.69 5.47-1.78.86-1.82.88-2.11.59-.14-.18-.72-.78-1.26-1.39l-.99-1.07.82-1.2c1.3-1.94 3.31-4.35 5.14-6.21.94-.94 1.58-1.71 1.44-1.71-.16.02-1.28.47-2.51 1.01z"
    })]
  }));
};

var _templateObject$1, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;

var pathsAreEqualIgnoreTrailingSlash = function pathsAreEqualIgnoreTrailingSlash(pathA, pathB) {
  return (pathA === null || pathA === void 0 ? void 0 : pathA.replace(/\/$/, '')) === (pathB === null || pathB === void 0 ? void 0 : pathB.replace(/\/$/, ''));
};

var ActiveNavigationLink = /*#__PURE__*/styled(Anchor).attrs(function (props) {
  var matchesCurrentLocation = typeof window !== 'undefined' && (pathsAreEqualIgnoreTrailingSlash(window.location.pathname, props.href) || window.location.pathname.indexOf(props.baseURL) === 0);
  var active = props.active || matchesCurrentLocation;
  return {
    active: active,
    'aria-current': active
  };
}).withConfig({
  componentId: "sc-15cpqmr-0"
})(["", " &,&:hover{text-decoration:none;}", ""], function (props) {
  return typographyMixin(Object.assign({
    fontType: 'body'
  }, props));
}, function (_ref) {
  var active = _ref.active;
  return css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteralLoose(["\n        &,\n        &:visited {\n            color: ", ";\n        }\n        &:hover {\n            color: ", ";\n        }\n    "])), token(active ? 'colors.brand' : 'colors.textDark'), token('colors.brand'));
});
var navLinkStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n    text-decoration: none;\n    &:hover {\n        text-decoration: none;\n    }\n    ", "\n    ", ";\n    ", ";\n"])), function (props) {
  return typographyMixin(Object.assign({
    fontType: 'body'
  }, props));
}, mediaMixin('md', css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n            margin: 0 ", ";\n        "])), spaceMixin('xs'))), mediaMixin('xl', css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n            padding: 0 ", ";\n        "])), spaceMixin('xs'))));
var NavigationAnchor = /*#__PURE__*/styled(ActiveNavigationLink).withConfig({
  componentId: "sc-15cpqmr-1"
})(["", ";"], navLinkStyles);
var subNavLinkStyles = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteralLoose(["\n    &&& {\n        padding-bottom: ", ";\n        margin-top: ", ";\n    }\n"])), spaceMixin('sm'), spaceMixin('sm'));
var StyledExternalSubNavLink = /*#__PURE__*/styled(NavigationAnchor).withConfig({
  componentId: "sc-15cpqmr-2"
})(["", ";", ";"], subNavLinkStyles, function (_ref2) {
  var active = _ref2.active;
  return active ? css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteralLoose(["\n                  &&& {\n                      color: ", ";\n                      border-bottom: 3px solid ", ";\n                  }\n              "])), token('colors.brand'), token('colors.brand')) : '';
});
var MobileNavLink = /*#__PURE__*/styled(ActiveNavigationLink).withConfig({
  componentId: "sc-15cpqmr-3"
})(["display:block;"]);

var PersistorContext = /*#__PURE__*/createContext(null);

var SignInLink = /*#__PURE__*/function (_Component) {
  _inheritsLoose(SignInLink, _Component);

  function SignInLink() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;

    _this.redirectToRoot = function () {
      var persistor = _this.context;
      persistor && persistor.flush().then(function () {
        if (window.location.pathname !== '/') {
          window.location.assign('/');
        }
      });
    };

    return _this;
  }

  var _proto = SignInLink.prototype;

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (!prevProps.didUserSessionExpire && this.props.didUserSessionExpire || !prevProps.didUserInvokeLogout && this.props.didUserInvokeLogout) {
      this.redirectToRoot();
    }
  };

  _proto.render = function render() {
    var _this$props = this.props,
        className = _this$props.className,
        authenticated = _this$props.authenticated,
        initiateLogin = _this$props.initiateLogin,
        logout = _this$props.logout;
    return /*#__PURE__*/jsx(NavigationAnchor, {
      className: className,
      onClick: authenticated ? logout : initiateLogin,
      children: authenticated ? 'Log out' : 'Sign in'
    });
  };

  return SignInLink;
}(Component);

SignInLink.propTypes = {
  className: PropTypes.string,
  authenticated: PropTypes.bool,
  logout: PropTypes.func.isRequired,
  initiateLogin: PropTypes.func.isRequired,
  didUserSessionExpire: PropTypes.bool,
  didUserInvokeLogout: PropTypes.bool
};
SignInLink.contextType = PersistorContext;

var SignIn = connect(function (state) {
  return {
    authenticated: authSelectors.isAuthenticatedSelector(state),
    didUserSessionExpire: authSelectors.didUserSessionExpireSelector(state),
    didUserInvokeLogout: authSelectors.didUserInvokeLogoutSelector(state)
  };
}, function (dispatch, _ref) {
  var newTab = _ref.newTab;
  return Object.assign({}, bindActionCreators({
    initiateLogin: authReducers[newTab ? 'initiateLoginInNewTab' : 'initiateLoginThunk'],
    logout: authReducers.logout
  }, dispatch));
})(SignInLink);

var _templateObject$2, _templateObject2$1;
var StyledSecondaryLabel = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-baymjn-0"
})(["", " ", ""], mediaMixin('lg', css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteralLoose(["\n            display: none;\n        "])))), mediaMixin('xxl', css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteralLoose(["\n            display: inline;\n        "])))));

var fireGA = function fireGA(loanType) {
  return trackEvent({
    category: 'Top nav',
    action: loanType + " Button Click",
    label: 'Button 1'
  });
};

var isLongFlowOpen = function isLongFlowOpen() {
  return typeof window !== 'undefined' && window.location.pathname.indexOf('get-started') !== -1;
};

var PurchaseNavigationLink = function PurchaseNavigationLink(_ref) {
  var longFlowLoanType = _ref.longFlowLoanType;
  return /*#__PURE__*/jsxs(NavigationAnchor, {
    active: isLongFlowOpen() && longFlowLoanType === constants.LOAN_TYPES.PURCHASE,
    href: "/pre-qualify/",
    onClick: function onClick() {
      fireGA(constants.LOAN_TYPES.PURCHASE);
    },
    children: ["Purchase", /*#__PURE__*/jsx(StyledSecondaryLabel, {
      children: " a home"
    })]
  });
};
PurchaseNavigationLink.propTypes = {
  longFlowLoanType: PropTypes.string
};
var RefinanceNavigationLink = function RefinanceNavigationLink(_ref2) {
  var longFlowLoanType = _ref2.longFlowLoanType;
  return /*#__PURE__*/jsxs(NavigationAnchor, {
    active: isLongFlowOpen() && longFlowLoanType === constants.LOAN_TYPES.REFINANCE,
    href: "/get-started/#/refinance",
    onClick: function onClick() {
      fireGA(constants.LOAN_TYPES.REFINANCE);
    },
    children: ["Refinance", /*#__PURE__*/jsx(StyledSecondaryLabel, {
      children: " my home"
    })]
  });
};
RefinanceNavigationLink.propTypes = {
  longFlowLoanType: PropTypes.string
};

var navigationLinkStateCallback = function navigationLinkStateCallback(state) {
  return {
    longFlowLoanType: longFlowSelectors.responseByKeySelector('loanType', state)
  };
};

var PurchaseNavigationLink$1 = connect(navigationLinkStateCallback)(PurchaseNavigationLink);
var RefinanceNavigationLink$1 = connect(navigationLinkStateCallback)(RefinanceNavigationLink);

var WWW_PRIMARY = [{
  label: 'Home',
  to: '/'
}, {
  label: 'Calculators',
  to: '/calculators/mortgage-calculator/',
  base: '/calculators/'
}, {
  label: 'Resources',
  to: '/resources/',
  external: true
}];
var PRIMARY_NAV_SECTION_SHAPE = PropTypes.shape({
  label: PropTypes.string,
  to: PropTypes.string,
  base: PropTypes.string,
  external: PropTypes.bool,
  render: PropTypes.func
});
var NAVIGATION_ITEMS = {
  www: {
    primary: WWW_PRIMARY,
    secondary: [{
      render: function render() {
        return /*#__PURE__*/jsx(PurchaseNavigationLink$1, {}, "Purchase");
      },
      key: 'Purchase'
    }, {
      render: function render() {
        return /*#__PURE__*/jsx(RefinanceNavigationLink$1, {}, "Refinance");
      },
      key: 'Refinance'
    }]
  },
  futureWWW: {
    primary: WWW_PRIMARY,
    secondary: [{
      label: 'Settings',
      to: '/settings/',
      requiresAuth: true
    }, {
      render: function render() {
        return /*#__PURE__*/jsx(SignIn, {}, "sign-in");
      },
      key: 'sign-in'
    }]
  },
  internal: {
    primary: [],
    secondary: [{
      render: function render() {
        return /*#__PURE__*/jsx(SignIn, {}, "sign-in");
      },
      key: 'sign-in'
    }]
  }
};
var PRIMARY_NAV_SHAPE = PropTypes.shape({
  primary: PropTypes.arrayOf(PRIMARY_NAV_SECTION_SHAPE),
  secondary: PropTypes.arrayOf(PRIMARY_NAV_SECTION_SHAPE)
});
var SUBNAV_ITEMS = {
  '/calculators/': {
    label: 'Calculators',
    items: [{
      label: 'Mortgage calculator',
      to: '/calculators/mortgage-calculator/'
    }, {
      label: 'Affordability calculator',
      to: '/calculators/affordability-calculator/'
    }, {
      label: 'Refinance calculator',
      to: '/calculators/refinance-calculator/'
    }]
  }
};
var SUB_NAV_ITEM_SHAPE = PropTypes.shape({
  to: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
});
var SUB_NAV_SHAPE = PropTypes.objectOf(PropTypes.shape({
  label: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(SUB_NAV_ITEM_SHAPE)
}));

var _templateObject$3;
var StyledGroupLabel = /*#__PURE__*/styled(Text).withConfig({
  componentId: "sc-omvehc-0"
})(["display:block;width:100%;padding-top:", ";padding-bottom:", ";", ";"], spaceMixin('md'), spaceMixin('md'), function (_ref) {
  var active = _ref.active;
  return active ? css(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteralLoose(["\n                  color: ", ";\n              "])), active && token('colors.brand')) : '';
});
var StyledExpandoContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-omvehc-1"
})(["padding-left:", ";padding-top:", ";padding-bottom:", ";margin-top:", ";margin-bottom:", ";border-left:1px solid ", ";color:", ";"], spaceMixin('md'), spaceMixin('xs'), spaceMixin('xs'), spaceMixin('sm'), spaceMixin('sm'), token('colors.borderLight'), token('colors.brand'));
var StyledNavigationGroupWrapper = /*#__PURE__*/styled(UnstyledButton).withConfig({
  componentId: "sc-omvehc-2"
})(["width:100%;display:flex;justify-content:space-between;align-items:center;padding:0 ", ";"], spaceMixin('md'));
var StyledNavigationGroup = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-omvehc-3"
})(["cursor:pointer;"]);
var StyledSubNav = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-omvehc-4"
})(["display:flex;flex-direction:column;padding:", " ", " ", " ", ";background-color:", ";"], spaceMixin('md'), spaceMixin('md'), spaceMixin('md'), spaceMixin('xl'), token('colors.backgroundLight'));
var MobileNavItem = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-omvehc-5"
})(["", ",", "{padding:", ";margin:0;}", "{display:block;}background-color:", ";border-bottom:1px solid ", ";"], MobileNavLink, NavigationAnchor, spaceMixin('md'), NavigationAnchor, token('colors.white'), token('colors.borderLight'));
var StyledSubNavLink = /*#__PURE__*/styled(MobileNavLink).withConfig({
  componentId: "sc-omvehc-6"
})(["& + &{padding-top:", ";}"], spaceMixin('md'));

var NavigationGroup = function NavigationGroup(_ref2) {
  var base = _ref2.base,
      openSubNav = _ref2.openSubNav,
      setOpenSubNavState = _ref2.setOpenSubNavState,
      label = _ref2.label,
      subnavItems = _ref2.subnavItems;
  var isSectionOpen = base === openSubNav;
  return /*#__PURE__*/jsxs(StyledNavigationGroup, {
    children: [/*#__PURE__*/jsxs(StyledNavigationGroupWrapper, {
      "aria-expanded": isSectionOpen,
      onClick: function onClick() {
        return setOpenSubNavState(isSectionOpen ? null : base);
      },
      children: [/*#__PURE__*/jsx(StyledGroupLabel, {
        active: isSectionOpen,
        children: label
      }), /*#__PURE__*/jsx(StyledExpandoContainer, {
        children: isSectionOpen ? /*#__PURE__*/jsx(IconChevronUp, {}) : /*#__PURE__*/jsx(IconChevronDown, {})
      })]
    }), isSectionOpen && /*#__PURE__*/jsx(StyledSubNav, {
      children: subnavItems[base].items.map(function (_ref3) {
        var subTo = _ref3.to,
            subLabel = _ref3.label;
        return /*#__PURE__*/jsx(StyledSubNavLink, {
          href: subTo,
          children: subLabel
        }, subTo);
      })
    })]
  });
};

NavigationGroup.propTypes = {
  base: PropTypes.string,
  label: PropTypes.string,
  openSubNav: PropTypes.string,
  subnavItems: SUB_NAV_SHAPE,
  setOpenSubNavState: PropTypes.func.isRequired
};

var MobileNav = function MobileNav(_ref4) {
  var openSubNav = _ref4.openSubNav,
      className = _ref4.className,
      primaryNav = _ref4.primaryNav,
      subnavItems = _ref4.subnavItems;

  var _useState = useState(openSubNav),
      _useState2 = _slicedToArray(_useState, 2),
      openSubNavState = _useState2[0],
      setOpenSubNavState = _useState2[1];

  useEffect(function () {
    setOpenSubNavState(openSubNav);
  }, [openSubNav]);
  return /*#__PURE__*/jsx("div", {
    className: className,
    children: primaryNav.primary.concat(primaryNav.secondary).map(function (_ref5) {
      var _ref6;

      var label = _ref5.label,
          to = _ref5.to,
          base = _ref5.base,
          key = _ref5.key,
          render = _ref5.render;
      return /*#__PURE__*/jsx(MobileNavItem, {
        children: base && subnavItems[base] ? /*#__PURE__*/jsx(NavigationGroup, {
          base: base,
          openSubNav: openSubNavState,
          setOpenSubNavState: setOpenSubNavState,
          label: label,
          subnavItems: subnavItems
        }) : render ? render() : /*#__PURE__*/jsx(MobileNavLink, {
          href: to,
          children: label
        }, to)
      }, (_ref6 = key !== null && key !== void 0 ? key : base) !== null && _ref6 !== void 0 ? _ref6 : to);
    })
  });
};

MobileNav.propTypes = {
  openSubNav: PropTypes.string,
  className: PropTypes.string,
  primaryNav: PRIMARY_NAV_SHAPE,
  subnavItems: SUB_NAV_SHAPE
};
MobileNav.defaultProps = {
  primaryNav: NAVIGATION_ITEMS.www,
  subnavItems: SUBNAV_ITEMS
};

var getSubNavItems = function getSubNavItems(currentPath) {
  var _SUBNAV_ITEMS$current;

  var currentNavigation = Object.keys(SUBNAV_ITEMS).find(function (base) {
    return currentPath.includes(base);
  });
  return (_SUBNAV_ITEMS$current = SUBNAV_ITEMS[currentNavigation]) !== null && _SUBNAV_ITEMS$current !== void 0 ? _SUBNAV_ITEMS$current : null;
};

var CurrentPathContext = /*#__PURE__*/createContext();

var useSubNavItems = function useSubNavItems() {
  var currentPath = useContext(CurrentPathContext);
  return useMemo(function () {
    var subNavItems = currentPath ? getSubNavItems(currentPath) : null;
    return subNavItems ? Object.assign({}, subNavItems, {
      items: subNavItems.items.map(function (item) {
        return Object.assign({}, item, {
          active: item.to === currentPath
        });
      })
    }) : null;
  }, [currentPath]);
};

var PageContext = /*#__PURE__*/createContext({
  isOffer: false
});

var _excluded$2 = ["navItems", "authenticated", "external"];

var _templateObject$4, _templateObject2$2, _templateObject3$1, _templateObject4$1, _templateObject5$1, _templateObject6$1, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12;
var StyledLogo = /*#__PURE__*/styled(Anchor).withConfig({
  componentId: "sc-98mzt3-0"
})(["display:block;margin:0 auto;svg,&{fill:", ";height:26px;width:245px;}", ""], token('colors.brand'), mediaMixin('md', css(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteralLoose(["\n            svg,\n            & {\n                height: 35px;\n                width: 320px;\n            }\n        "])))));
var OfferLogo = /*#__PURE__*/styled(StyledLogo).withConfig({
  componentId: "sc-98mzt3-1"
})(["", ";"], mediaMixin('lg', css(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteralLoose(["\n            margin: 0;\n        "])))));
var StyledLogoContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-98mzt3-2"
})(["flex-basis:100%;", ";"], mediaMixin('lg', css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteralLoose(["\n            flex-basis: 34%;\n        "])))));
var StyledHeaderContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-98mzt3-3"
})(["width:100%;background-color:", ";border-bottom:1px solid ", ";padding:", " 0;height:", ";", ";"], token('colors.white'), token('colors.borderLight'), spaceMixin('sm'), token('sizing.headerHeightMobile'), mediaMixin('md', css(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteralLoose(["\n            height: ", ";\n        "])), token('sizing.headerHeight'))));
var StyledHeader = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-98mzt3-4"
})(["display:flex;", ";max-width:", ";height:100%;align-items:center;margin:0 auto;"], mediaMixin('lg', css(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteralLoose(["\n            justify-content: space-between;\n        "])))), token('sizing.headerWidth'));
var StyledOfferHeader = /*#__PURE__*/styled(StyledHeader).withConfig({
  componentId: "sc-98mzt3-5"
})(["", ";"], mediaMixin('lg', css(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteralLoose(["\n            padding: 0 ", ";\n        "])), spaceMixin('sm'))));
var StyledNavSection = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-98mzt3-6"
})(["flex-basis:33%;display:none;", ";", ";"], function (_ref) {
  var rightAlign = _ref.rightAlign;
  return rightAlign && 'text-align: right;';
}, mediaMixin('lg', css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteralLoose(["\n            display: block;\n        "])))));
var StyledSubNavContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-98mzt3-7"
})(["width:100%;margin:0 auto;display:none;", ";max-width:", ";"], mediaMixin('lg', css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteralLoose(["\n            display: flex;\n        "])))), token('sizing.headerWidth'));

var SubNav = function SubNav(_ref2) {
  var label = _ref2.label,
      className = _ref2.className,
      renderLink = _ref2.renderLink,
      items = _ref2.items;
  return /*#__PURE__*/jsx("nav", {
    "aria-label": label,
    className: className,
    children: /*#__PURE__*/jsx(StyledSubNavContainer, {
      children: items.map(renderLink)
    })
  });
};

SubNav.propTypes = {
  label: PropTypes.string.isRequired,
  renderLink: PropTypes.func.isRequired,
  match: PropTypes.object,
  className: PropTypes.string,
  items: PropTypes.arrayOf(SUB_NAV_ITEM_SHAPE),
  getItemIsActive: PropTypes.func,
  currentPath: PropTypes.string,
  currentHash: PropTypes.string
};
SubNav.defaultProps = {
  renderLink: function renderLink(link) {
    return /*#__PURE__*/jsx(StyledExternalSubNavLink, {
      href: link.to,
      exact: true,
      active: link.active,
      children: link.label
    }, link.to);
  }
};
var StyledSubNav$1 = /*#__PURE__*/styled(SubNav).withConfig({
  componentId: "sc-98mzt3-8"
})(["width:100%;border-bottom:1px solid ", ";"], token('colors.borderLight'));
var mobileMenuIconStyles = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteralLoose(["\n    display: block;\n    cursor: pointer;\n    margin-left: ", ";\n    ", "\n"])), spaceMixin('xs'), mediaMixin('lg', css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteralLoose(["\n            display: none;\n            margin-left: 0;\n        "])))));
var StyledMenuIcon = /*#__PURE__*/styled(IconMenu).withConfig({
  componentId: "sc-98mzt3-9"
})(["", ""], mobileMenuIconStyles);
var StyledCloseIcon = /*#__PURE__*/styled(IconClose).withConfig({
  componentId: "sc-98mzt3-10"
})(["", ""], mobileMenuIconStyles);

var HeaderNavSection = function HeaderNavSection(_ref3) {
  var navItems = _ref3.navItems,
      authenticated = _ref3.authenticated,
      external = _ref3.external,
      navProps = _objectWithoutProperties(_ref3, _excluded$2);

  return /*#__PURE__*/jsx(StyledNavSection, Object.assign({}, navProps, {
    children: navItems.filter(function (_ref4) {
      var requiresAuth = _ref4.requiresAuth;
      return !requiresAuth || requiresAuth && authenticated;
    }).map(function (nav) {
      return nav.render ? nav.render() : /*#__PURE__*/jsx(NavigationAnchor, {
        href: nav.to,
        baseURL: nav.base,
        children: nav.label
      }, nav.to);
    })
  }));
};

HeaderNavSection.propTypes = {
  external: PropTypes.bool,
  authenticated: PropTypes.bool,
  navItems: PropTypes.arrayOf(PropTypes.shape({
    base: PropTypes.string,
    to: PropTypes.string,
    label: PropTypes.string
  })),
  rightAlign: PropTypes.bool
};
var StyledMobileNav = /*#__PURE__*/styled(MobileNav).withConfig({
  componentId: "sc-98mzt3-11"
})(["display:", ";background-color:", ";width:100%;position:absolute;top:", ";", ";bottom:0;left:0;z-index:", ";"], function (_ref5) {
  var open = _ref5.open;
  return open ? 'block' : 'none';
}, token('colors.white'), token('sizing.headerHeightMobile'), mediaMixin('md', css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteralLoose(["\n            top: ", ";\n        "])), token('sizing.headerHeight'))), token('zIndexing.header'));
var StyledPhoneContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-98mzt3-12"
})(["align-items:center;display:flex;"]);
var StyledPhoneSection = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-98mzt3-13"
})(["display:none;", ""], mediaMixin('lg', css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteralLoose(["\n            display: flex;\n            justify-content: flex-end;\n        "])))));

var OfferHeader = function OfferHeader() {
  return /*#__PURE__*/jsxs(StyledOfferHeader, {
    children: [/*#__PURE__*/jsx(StyledLogoContainer, {
      children: /*#__PURE__*/jsx(OfferLogo, {
        href: "/",
        "aria-label": "Zillow Home Loans",
        children: /*#__PURE__*/jsx(SvgZhlLogo, {})
      })
    }), /*#__PURE__*/jsx(StyledPhoneSection, {
      children: /*#__PURE__*/jsxs(StyledPhoneContainer, {
        children: [/*#__PURE__*/jsx(DetailedIconPhone, {}), /*#__PURE__*/jsx(Spacer, {
          marginLeft: "md"
        }), /*#__PURE__*/jsx(TextButton, {
          as: "a",
          href: constants.ZHL_OFFER_NUMBER_TEL,
          children: constants.ZHL_OFFER_NUMBER
        })]
      })
    })]
  });
};

var StandardHeader = function StandardHeader(_ref6) {
  var onHamburgerClick = _ref6.onHamburgerClick,
      mobileOpen = _ref6.mobileOpen,
      external = _ref6.external,
      primaryNav = _ref6.primaryNav,
      authenticated = _ref6.authenticated;
  var HeaderMenuIcon = mobileOpen ? StyledCloseIcon : StyledMenuIcon;
  return /*#__PURE__*/jsxs(StyledHeader, {
    children: [/*#__PURE__*/jsx(HeaderNavSection, {
      external: external,
      navItems: primaryNav.primary
    }), /*#__PURE__*/jsx(UnstyledButton, {
      onClick: onHamburgerClick,
      "aria-label": mobileOpen ? 'Close navigation menu' : 'Open navigation menu',
      children: /*#__PURE__*/jsx(HeaderMenuIcon, {
        size: "sm",
        fontColor: "brand",
        "aria-hidden": "true"
      })
    }), /*#__PURE__*/jsx(StyledMobileNav, {
      open: mobileOpen,
      primaryNav: primaryNav
    }), /*#__PURE__*/jsx(StyledLogoContainer, {
      children: /*#__PURE__*/jsx(StyledLogo, {
        href: "/",
        "aria-label": "Zillow Home Loans",
        children: /*#__PURE__*/jsx(SvgZhlLogo, {})
      })
    }), /*#__PURE__*/jsx(HeaderNavSection, {
      authenticated: authenticated,
      external: external,
      navItems: primaryNav.secondary,
      rightAlign: true
    })]
  });
};

StandardHeader.propTypes = {
  primaryNav: PRIMARY_NAV_SHAPE,
  external: PropTypes.bool,
  // For use by wordpress
  onHamburgerClick: PropTypes.func,
  mobileOpen: PropTypes.bool,
  authenticated: PropTypes.bool
};

var Header = function Header(_ref7) {
  var className = _ref7.className,
      onHamburgerClick = _ref7.onHamburgerClick,
      mobileOpen = _ref7.mobileOpen,
      external = _ref7.external,
      isPassportOffer = _ref7.isPassportOffer,
      renderLink = _ref7.renderLink,
      primaryNav = _ref7.primaryNav,
      authenticated = _ref7.authenticated;
  var subNavItems = useSubNavItems();

  var _useContext = useContext(PageContext),
      isOffer = _useContext.isOffer;

  var showPassportOffer = isPassportOffer && isOffer;
  return /*#__PURE__*/jsxs("header", {
    className: className,
    children: [/*#__PURE__*/jsx(StyledHeaderContainer, {
      children: showPassportOffer ? /*#__PURE__*/jsx(OfferHeader, {}) : /*#__PURE__*/jsx(StandardHeader, {
        primaryNav: primaryNav,
        external: external,
        onHamburgerClick: onHamburgerClick,
        mobileOpen: mobileOpen,
        authenticated: authenticated
      })
    }), !showPassportOffer && subNavItems && /*#__PURE__*/jsx(StyledSubNav$1, Object.assign({
      renderLink: renderLink
    }, subNavItems))]
  });
};

Header.propTypes = {
  renderLink: PropTypes.func,
  primaryNav: PRIMARY_NAV_SHAPE,
  external: PropTypes.bool,
  // For use by wordpress
  onHamburgerClick: PropTypes.func,
  isPassportOffer: PropTypes.bool,
  mobileOpen: PropTypes.bool,
  className: PropTypes.string,
  authenticated: PropTypes.bool
};
Header.defaultProps = {
  primaryNav: NAVIGATION_ITEMS.www
};
Header.SubNav = StyledSubNav$1;

var headerStateCallback = function headerStateCallback(state) {
  return {
    authenticated: authSelectors.isAuthenticatedSelector(state),
    isPassportOffer: queryParamsSelectors.isPassportSourceSelector(state)
  };
};

var Header$1 = connect(headerStateCallback)(Header);

var _excluded$3 = ["title"];

var SvgEhlLogo = function SvgEhlLogo(_ref) {
  var title = _ref.title,
      props = _objectWithoutProperties(_ref, _excluded$3);

  return /*#__PURE__*/jsxs("svg", Object.assign({
    viewBox: "0 0 80 87"
  }, props, {
    children: [title ? /*#__PURE__*/jsx("title", {
      children: title
    }) : null, /*#__PURE__*/jsx("path", {
      d: "M35.8 80.6l-4.6-9.3h-2.5v13.4h2.2v-9.6l4.7 9.6H38V71.3h-2.2v9.3zM25.1 61.9l-2 7.1h1.3l.4-1.5h2.1l.4 1.5h1.3l-2-7.2c-.1.1-1.5.1-1.5.1zm0 4.4l.7-2.8.7 2.8h-1.4zm-8.4-.9c0-3.4-2-3.8-2.8-3.8-.8 0-2.8.4-2.8 3.8s2 3.8 2.8 3.8c.3 0 .9-.1 1.5-.5l.7.8.6-.8-.6-.7c.4-.6.6-1.4.6-2.6zM15.3 67l-.6-.7-.7.8.6.7c-.2.1-.4.2-.6.2-.6 0-1.6-.5-1.6-2.5S13.3 63 14 63c.7 0 1.6.5 1.6 2.5-.1.6-.2 1.1-.3 1.5zm14-5.1V69h4.1v-1.3h-2.9v-5.9l-1.2.1zM42.1 69h1.2v-7.2h-1.2v2.7h-2.3v-2.7h-1.2V69h1.2v-3.2h2.3V69zm-22.6 9.8H25v-2.4h-5.6v-2.8h6.1v-2.4h-8.4v13.4h8.7v-2.4h-6.3v-3.4zm-9.2-15.7v-1.3H6.1V69h4.4v-1.3H7.2v-1.9H10v-1.3H7.2V63c0 .1 3.1.1 3.1.1zm-1.6 8.2H6.3v13.4h8.1v-2.4H8.7v-11zm9-9.4v4.7c0 .7.1 1.3.4 1.7.5.7 1.3.9 1.8.9 1.5 0 2.4-.8 2.4-2.7v-4.7h-1.2v4.7c0 .9-.3 1.4-1.2 1.4-.6 0-1-.4-1-1.4v-4.6h-1.2zM61.9 69h1.2v-7.2h-1.2V69zm12.8 12.6c0-2.5-.5-2.9-1.5-3.4 1.1-.4 1.8-1.7 1.8-3.2 0-1.2-.6-3.8-3.6-3.8h-5.8v13.4H68v-5.2h2.5c1.8 0 1.9.7 1.9 2.5 0 1.4.1 2.1.3 2.7h2.6v-.4c-.6-.1-.6-.6-.6-2.6zm-3.9-4.4H68v-3.6h3c1.4 0 1.7 1 1.7 1.8-.1 1.3-.7 1.8-1.9 1.8zM58.7 68c-1.1 0-1.2-.8-1.2-1.2h-1.2c0 .9.4 2.4 2.5 2.4.5 0 2.3-.2 2.3-2.3 0-1.3-.7-1.7-1.5-1.9l-1.4-.4c-.2-.1-.6-.2-.6-.7 0-.6.5-.9 1-.9 1.1 0 1.2.8 1.2 1.1H61c0-.9-.5-2.3-2.3-2.3-1.9 0-2.2 1.4-2.2 2.2 0 1.6.9 1.8 1.8 2.1l.6.2c.6.2 1 .3 1 .9 0 .6-.7.8-1.2.8zm10.1 1v-7.2h-1.1v5l-2.3-5h-1.3V69h1.1v-5.1l2.4 5.1h1.2zm3.8-6.1c.9 0 1.3.9 1.3 1.2h1.2c-.1-1-.8-2.4-2.5-2.4-1.9 0-2.8 1.8-2.8 3.8 0 2.5 1.3 3.7 2.7 3.7 1.1 0 1.5-.6 1.8-1.1l.1.9h.8v-3.9h-2.4v1.2h1.3c-.3 1.4-1.1 1.6-1.4 1.6-.8 0-1.7-.5-1.7-2.5 0-1.7.8-2.5 1.6-2.5zm-17.2 3.6v-4.7h-1.2v4.7c0 .9-.3 1.4-1.2 1.4-.6 0-1-.4-1-1.4v-4.6h-1.2v4.7c0 .7.1 1.3.5 1.7.5.7 1.3.9 1.8.9 1.4 0 2.3-.8 2.3-2.7zm.9 12.3h5.6v-2.4h-5.6v-2.8h6.1v-2.4H54v13.4h8.7v-2.4h-6.3l-.1-3.4zm-6.5-13.4c0-3.4-2-3.8-2.8-3.8-.8 0-2.8.4-2.8 3.8s2 3.8 2.8 3.8c.8 0 2.8-.4 2.8-3.8zm-4.3 0c0-2 .9-2.5 1.6-2.5.7 0 1.6.5 1.6 2.5s-.9 2.5-1.6 2.5c-.7 0-1.6-.5-1.6-2.5zm.9 5.9h-5v13.4h5c3.8 0 4.7-4.1 4.7-7 0-2.6-.8-6.4-4.7-6.4zm-.3 11h-2.3v-8.8H46c1 0 2.6.3 2.6 4.3.1 2.3-.6 4.5-2.5 4.5zM40 2.3l-37.1 19V29l4.1.1v27.7h65.7V29.1h4.4v-7.7L40 2.3zm25.4 47.2H14.3V24.9L40 11.6l25.4 13.2v24.7zM27.9 31.3H52v-6.6H27.9v6.6zm0 12h24v-6.6h-24v6.6z"
    })]
  }));
};

var _excluded$4 = ["title"];

var SvgFooterSkyline = function SvgFooterSkyline(_ref) {
  var title = _ref.title,
      props = _objectWithoutProperties(_ref, _excluded$4);

  return /*#__PURE__*/jsxs("svg", Object.assign({
    id: "footer-skyline_svg__Layer_1",
    "data-name": "Layer 1",
    viewBox: "0 0 1200 160"
  }, props, {
    children: [title ? /*#__PURE__*/jsx("title", {
      children: title
    }) : null, /*#__PURE__*/jsx("defs", {
      children: /*#__PURE__*/jsx("style", {
        children: '.footer-skyline_svg__cls-1{fill:#0c4499}.footer-skyline_svg__cls-3{fill:#f2af34}.footer-skyline_svg__cls-4{fill:#136ffb}.footer-skyline_svg__cls-5{fill:#c1edfe}.footer-skyline_svg__cls-6{fill:#fff}.footer-skyline_svg__cls-7{fill:#bfecff}.footer-skyline_svg__cls-9{fill:#c1e8fa}.footer-skyline_svg__cls-10{fill:#194995}.footer-skyline_svg__cls-11{fill:#c0e7fa}'
      })
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M97.91 143.14c4.21-3.25 9.54 6.6 9.54 6.6s-7-15.06-.8-15.73c2.57-.28 5.33 6.29 6.65 12.69 0-5.9 1.48-10.81 3.58-11 6.19-.64 2.39 15.56 2.39 15.56l-1.62 8.74H108s-14.2-13.69-10.09-16.86z"
    }), /*#__PURE__*/jsx("path", {
      d: "M815.19 151.49c.27-.19.53-.39.79-.59a20 20 0 0 0 6.44-10.42c1.29-4.49 1.53-9.58-1.17-14.15-7.73-13.08-23-6-29 1a15.63 15.63 0 0 0-3.31 15 18.62 18.62 0 0 0 13.7 11.75 16.81 16.81 0 0 0 12.55-2.59z",
      fill: "#fed036"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M805.68 131.03h.85v28.96h-.85z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-3",
      d: "M507.78 149.37c-1.15-1.53-3.53-1.11-4.69.37a7.06 7.06 0 0 0-1.43 3.55c.06-3.75-.23-12.51-4.67-12.29-3.25.17-3.32 8.37-3.16 12.31-1.33-4.09-3-5.95-5.29-5.92-5.68.05 1.06 12.61 1.06 12.61h14.8c.46 0 1.76-2.7 2-3.15a19.24 19.24 0 0 0 1.68-3.44 5.32 5.32 0 0 0 .21-2.85 3 3 0 0 0-.51-1.19z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M255.52 142c2.24-.25 4.65 5.48 5.8 11.07 0-5.15 1.29-9.43 3.12-9.62 5.4-.55 2.09 8.9 2.09 8.9l-1.41 7.65h-8.39s-6.6-17.4-1.21-18zM834.52 142c2.24-.25 4.65 5.48 5.8 11.07 0-5.15 1.29-9.43 3.12-9.62 5.4-.55 2.09 8.9 2.09 8.9l-1.41 7.65h-8.39s-6.6-17.4-1.21-18zM688.47 138.42c.25-6.58-3.3-33.32-15-28.94-3.77 1.41-7.16 7.19-8.83 10.59-4.23 8.58-6.83 27.2 2.37 33.77 4.7 3.36 11.68 2.2 15.91-1.73 3.84-3.59 5.35-8.52 5.55-13.69z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M673.79 125.68h1.01v34.31h-1.01z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-3",
      d: "M1141.47 142.62c-.82-3.39-3-6.33-3.81-9.72-.77-3.17-.34-6.67-1.84-9.57-1.34-2.6-2.77-3.35-5.69-3.33s-4.35.73-5.69 3.33c-1.49 2.9-1.07 6.4-1.84 9.57-.82 3.39-3 6.33-3.81 9.72l-.06.3c-1.42 6.66 2.32 12.91 9.13 12.91h3.14a10.76 10.76 0 0 0 10.59-12.91c-.07-.1-.09-.2-.12-.3z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1129.73 132.55h.81v27.44h-.81zM199.48 54.45l-38.52 62.5H238l-38.52-62.5z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M149.26 54.45h50.22v62.49h-50.22z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M149.26 54.45l-38.52 62.5h77.04l-38.52-62.5z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-5",
      d: "M149.26 61.3l-34.29 55.65h68.59l-34.3-55.65zM114.96 116.95h68.59v20.56h-68.59z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-5",
      d: "M165.18 116.95h68.59v20.56h-68.59z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M130.06 122.36h13.9v9.82h-13.9zM211.14 122.36h13.9v9.82h-13.9z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M114.96 137.51h118.81V160H114.96z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M146.14 137.51h54.53V160h-54.53z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M178.36 100.82l-22.31 36.69h44.62l-22.31-36.69z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M168.45 100.82h9.91v9.27h-9.91z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M168.45 100.82l-22.31 36.69h44.62l-22.31-36.69z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M168.45 110.09l-13.2 21.88h26.4l-13.2-21.88z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M121.28 140.63h20.66v13.53h-20.66zM208.57 140.63h20.66v13.53h-20.66z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M146.14 137.34h44.62V160h-44.62z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M168.42 137.51h.06a7.42 7.42 0 0 1 7.42 7.42V160H161v-15.07a7.42 7.42 0 0 1 7.42-7.42z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M143.47 88.82h11.59v17.04h-11.59z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M857.74 8.72h92.83v151.4h-92.83z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M950.56 8.72h22.95v151.4h-22.95z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-7",
      d: "M867.69 18.92h13.39v26.43h-13.39zM894.17 18.92h13.39v26.43h-13.39zM867.69 52.08h13.39v26.43h-13.39zM894.17 52.08h13.39v26.43h-13.39zM867.69 84.98h13.39v26.43h-13.39zM894.17 84.98h13.39v26.43h-13.39zM924.11 18.92h13.39v26.43h-13.39zM924.11 52.08h13.39v26.43h-13.39zM924.11 84.98h13.39v26.43h-13.39z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M883.29 143.92h31.28v16.2h-31.28z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M903.63 127.26c6 0 10.94 5.7 10.94 12.73v3.93h-29.26v-16.66z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-7",
      d: "M885.31 127.26A12.73 12.73 0 0 0 872.58 140v3.93H898V140a12.72 12.72 0 0 0-12.69-12.74zM848.05 0h102.52v8.72H848.05z"
    }), /*#__PURE__*/jsx("path", {
      fill: "#006aff",
      d: "M950.56 0h22.95v8.72h-22.95z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M1013.91 86.66h59.08V160h-59.08z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1072.99 86.66h26.97V160h-26.97z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M1081.49 117.95h32.81V160h-32.81z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1114.31 117.95h11.58V160h-11.58z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M1023.99 98.11h39.6v16.84h-39.6z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M1031.54 98.11h24.5v16.84h-24.5zM1095.48 130h18.83v23.44h-18.83z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M1114.31 130h7.91v23.44h-7.91z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M1023.99 137.21h13.2v16.8h-13.2z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M1023.99 154.01h13.2V160h-13.2z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1044.87 132.84h18.72V160h-18.72z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M1048.49 137.21h15.1V160h-15.1zM1010.37 123.33h49.42v3.32h-49.42zM1010.37 85h62.62v3.32h-62.62z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M1072.99 85h29.09v3.32h-29.09z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1058.59 123.33h7.2v3.32h-7.2z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M1083.44 112.55h.9v5.4h-.9zM1086.6 112.55h.9v5.4h-.9zM1089.77 112.55h.9v5.4h-.9zM1092.93 112.55h.9v5.4h-.9zM1096.09 112.55h.9v5.4h-.9zM1099.25 112.55h.9v5.4h-.9zM1102.41 112.55h.9v5.4h-.9zM1105.57 112.55h.9v5.4h-.9zM1108.73 112.55h.9v5.4h-.9zM1111.89 112.55h.9v5.4h-.9z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1115.43 112.55h.9v5.4h-.9zM1118.62 112.55h.9v5.4h-.9zM1121.77 112.55h.9v5.4h-.9z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M1081.49 111.15h32.81v1.4h-32.81z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M1114.31 111.15h11.58v1.4h-11.58z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M685.74 122.62h98.4v33.04h-98.4z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M797.35 122.62H685.74l2.9-40.21h105.81l2.9 40.21z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M735.99 84.57l23.58 28.86h-47.16l23.58-28.86z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M763.09 115.1h-54.21L736 81.93zm-47.16-3.34H756l-20-24.55z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M732.56 95.69h7.22v10.64h-7.22zM701.68 127.81h20.66v18.69h-20.66zM762.49 133.06h16.09v14.15h-16.09z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M740.54 134.44h11.73v21.07h-11.73z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-10",
      d: "M767 76h11.57v22.26H767z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-11",
      d: "M711.12 125.59h2.08v23.31h-2.08z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-11",
      transform: "rotate(-90 712.155 137.245)",
      d: "M711.12 125.59h2.08v23.31h-2.08z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M736.2 120.19h49.34v3.76H736.2z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      transform: "rotate(90 735.24 138.07)",
      d: "M717.68 136.68h35.12v2.78h-35.12z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      transform: "rotate(90 756.79 138.07)",
      d: "M739.23 136.68h35.12v2.78h-35.12z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      transform: "rotate(90 784.14 138.28)",
      d: "M766.58 136.89h35.12v2.78h-35.12z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M712.78 92.95l23.85 28.39h-47.69l23.84-28.39z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M740.22 123h-54.87l27.43-32.66zm-47.7-3.34h40.53l-20.27-24.12z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M708.83 104.41h7.92v11.66h-7.92z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-10",
      d: "M785.47 120.29h11.89v35.57h-11.89z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M787.67 134.44h6.61v11.4h-6.61z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-10",
      d: "M685.74 155.5h111.61v4.44H685.74z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M505.35 72h102.56v88H505.35z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M505.35 146.04h102.56v.75H505.35zM505.35 139.78h102.56v.75H505.35zM505.35 133.52h102.56v.75H505.35zM505.35 127.25h102.56v.75H505.35zM505.35 120.99h102.56v.75H505.35zM505.35 114.73h102.56v.75H505.35zM505.35 108.47h102.56v.75H505.35zM505.35 102.21h102.56v.75H505.35zM505.35 95.94h102.56v.75H505.35zM505.35 89.68h102.56v.75H505.35zM505.35 83.42h102.56v.75H505.35zM505.35 77.16h102.56v.75H505.35zM615.63 106.13h22.93V160h-22.93z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M505.35 72h37.23v37.23h-37.23z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-5",
      d: "M497.12 102.96h45.46v6.46h-45.46z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M525.56 127.35h17.02v28.92h-17.02z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-5",
      d: "M547.09 129.56h7.84v7.84h-7.84zM560.41 129.56h7.84v7.84h-7.84zM582.64 86.14h7.84v7.84h-7.84zM595.95 86.14h7.84v7.84h-7.84zM521.68 125.64h20.9v3.92h-20.9z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M615.63 153.95h22.93v.75h-22.93zM615.63 147.69h22.93v.75h-22.93zM615.63 141.42h22.93v.75h-22.93zM615.63 135.16h22.93v.75h-22.93zM615.63 128.9h22.93v.75h-22.93zM615.63 122.64h22.93v.75h-22.93zM615.63 116.37h22.93v.75h-22.93z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M577.76 106.13h37.88V160h-37.88z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M577.76 153.95h37.88v.75h-37.88zM577.76 147.69h37.88v.75h-37.88zM577.76 141.42h37.88v.75h-37.88zM577.76 135.16h37.88v.75h-37.88zM577.76 128.9h37.88v.75h-37.88zM577.76 122.64h37.88v.75h-37.88zM577.76 116.37h37.88v.75h-37.88z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M505.35 97.8h37.23v.75h-37.23zM505.35 91.54h37.23v.75h-37.23zM505.35 85.27h37.23v.75h-37.23zM505.35 79.01h37.23v.75h-37.23z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-5",
      d: "M573.84 105.4h45.54v6.46h-45.54z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M617.39 105.4h27.06v6.46h-27.06z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-5",
      d: "M497.12 156.28h80.63V160h-80.63z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M386.57 0h43.82v23.79h-43.82z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M303.91 0h82.83v23.79h-82.83z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M294.81 16.3h94.9V160h-94.9z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M386.6 16.3h54.74V160H386.6z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M294.81 52.41h91.93v3.57h-91.93zM294.81 85.11h91.93v3.57h-91.93zM294.81 118.87h91.93v3.57h-91.93z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-9",
      d: "M298.87 22.91h83.8v27.66h-83.8zM298.87 57.81h83.8v25.17h-83.8zM298.87 90.8h83.8v25.17h-83.8zM299.52 125.25h82.81v34.39h-82.81z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M354.63 17.48h3.57v33.45h-3.57zM323.36 17.48h3.57v33.45h-3.57zM354.63 56.48h3.57v27.21h-3.57zM323.36 56.25h3.57v27.21h-3.57zM354.63 89.78h3.57v27.21h-3.57zM323.36 89.55h3.57v27.21h-3.57zM392.34 90.65h43.77v25.17h-43.77zM392.34 57.81h43.77v25.17h-43.77zM391.94 22.91h44.57v27.66h-44.57z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-6",
      d: "M325.52 160v-20.48h30.49V160"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M357.8 160h-3.57v-18.7h-26.92V160h-3.57v-22.26h34.06V160z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M476.68 143.14c-4.21-3.25-9.54 6.6-9.54 6.6s7-15.06.8-15.73c-2.57-.28-5.33 6.29-6.65 12.69 0-5.9-1.48-10.81-3.58-11-6.19-.64-2.39 15.56-2.39 15.56l1.61 8.76h9.62s14.24-13.71 10.13-16.88z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-3",
      d: "M1003.78 149.37c-1.15-1.53-3.53-1.11-4.69.37a7.06 7.06 0 0 0-1.43 3.55c.06-3.75-.23-12.51-4.67-12.29-3.25.17-3.32 8.37-3.16 12.31-1.33-4.09-3-5.95-5.29-5.92-5.68.05 1.06 12.61 1.06 12.61h14.8c.46 0 1.76-2.7 2-3.15a19.24 19.24 0 0 0 1.68-3.44 5.32 5.32 0 0 0 .21-2.85 3 3 0 0 0-.51-1.19z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1153.63 144.89q.48.26 1 .48a28 28 0 0 0 13.72 2c5.29-.4 10.79-1.87 14.51-5.66 10.65-10.83-3.18-21.37-13.17-24.05-5.77-1.55-12.25-1.09-17.25 2.17s-8.13 9.59-6.75 15.4a15.44 15.44 0 0 0 7.94 9.66z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1156.87 122.28l.7.4a18.64 18.64 0 0 0 10.14 1.63c3.9-.33 8-1.55 10.71-4.68 7.86-9-2.35-17.7-9.73-19.92a14.49 14.49 0 0 0-12.74 1.8 13.08 13.08 0 0 0-5 12.75 12.53 12.53 0 0 0 5.92 8.02z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M1164.73 122.96h1.09v37.03h-1.09z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-4",
      d: "M66.3 137.2c-1.41-5.85-5.15-10.93-6.57-16.77-1.33-5.47-.6-11.5-3.18-16.52a14 14 0 0 0-24.65 0c-2.58 5-1.85 11-3.18 16.52-1.42 5.84-5.16 10.92-6.57 16.77a22.61 22.61 0 0 0 8.18 22.8h27.8a22.6 22.6 0 0 0 8.17-22.8z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-1",
      d: "M44.67 112.63h1.39v47.36h-1.39z"
    }), /*#__PURE__*/jsx("path", {
      className: "footer-skyline_svg__cls-3",
      d: "M302.78 149.37c-1.15-1.53-3.53-1.11-4.69.37a7.06 7.06 0 0 0-1.43 3.55c.06-3.75-.23-12.51-4.67-12.29-3.25.17-3.32 8.37-3.16 12.31-1.33-4.09-3-5.95-5.29-5.92-5.68.05 1.06 12.61 1.06 12.61h14.8c.46 0 1.76-2.7 2-3.15a19.24 19.24 0 0 0 1.68-3.44 5.32 5.32 0 0 0 .21-2.85 3 3 0 0 0-.51-1.19zM83.63 142.65c-1.58-2.49-4.84-1.81-6.42.61a12.89 12.89 0 0 0-2 5.79c.08-6.12-.32-20.41-6.39-20-4.45.26-4.55 13.64-4.33 20.07-1.81-6.68-4.07-9.7-7.23-9.66-7.78.09 1.44 20.58 1.44 20.58H79c.63 0 2.42-4.41 2.76-5.14a33.94 33.94 0 0 0 2.31-5.62 10.43 10.43 0 0 0 .28-4.65 5.65 5.65 0 0 0-.72-1.98z"
    })]
  }));
};

//This script is copied from https://bitbucket.zgtools.net/projects/FE/repos/page-frame-content/browse/src/client-assets/scripts/oneTrust.js
var OPTANON_SCRIPT_ELEMENT_ID = 'optanon-script';
var ONETRUST_CSS_ELEMENT_ID = 'onetrust-css';
var optanonScript = 'https://cdn.cookielaw.org/langswitch/44e5a498-b59b-4eb4-acc5-93d78e92deea.js';
var optanonCss = 'https://optanon.blob.core.windows.net/skins/5.3.0/default_flat_bottom_two_button_black/v2/css/optanon.css';
/* eslint-disable no-undef */

var setUpOptanonBanner = function setUpOptanonBanner() {
  // one trust script may not finish loading yet
  if ('undefined' === typeof Optanon) {
    setTimeout(function () {
      setUpOptanonBanner();
    }, 100);
  } else {
    Optanon.loadDefaultBannerGroup();
    Optanon.LoadBanner();
    setTimeout(function () {
      Optanon.ToggleInfoDisplay();
    }, 50);
  }
};

var loadOneTrustJSScript = function loadOneTrustJSScript() {
  if (!document.getElementById(OPTANON_SCRIPT_ELEMENT_ID)) {
    var optanonSetUpScript = document.createElement('script');
    optanonSetUpScript.id = OPTANON_SCRIPT_ELEMENT_ID;
    optanonSetUpScript.src = optanonScript;
    optanonSetUpScript.type = 'text/javascript';

    optanonSetUpScript.onload = function () {
      setUpOptanonBanner();
    };

    document.body.appendChild(optanonSetUpScript);
  }
};

var oneTrustAssetInitialSetup = function oneTrustAssetInitialSetup() {
  if (!document.getElementById(ONETRUST_CSS_ELEMENT_ID)) {
    var oneTrustCss = document.createElement('link');
    oneTrustCss.id = ONETRUST_CSS_ELEMENT_ID;
    oneTrustCss.rel = 'stylesheet';
    oneTrustCss.href = optanonCss;

    oneTrustCss.onload = function () {
      loadOneTrustJSScript();
    };

    document.body.appendChild(oneTrustCss);
  }
};

var setupOneTrustFooter = function setupOneTrustFooter(CookiePreferenceComponent) {
  return /*#__PURE__*/jsx(CookiePreferenceComponent, {
    onClick: function onClick() {
      if ('undefined' === typeof Optanon) {
        oneTrustAssetInitialSetup();
      } else {
        Optanon.ToggleInfoDisplay();
      }
    }
  });
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __values(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

/**
 * Session Status
 */
var SessionStatus;
(function (SessionStatus) {
    /** JSDoc */
    SessionStatus["Ok"] = "ok";
    /** JSDoc */
    SessionStatus["Exited"] = "exited";
    /** JSDoc */
    SessionStatus["Crashed"] = "crashed";
    /** JSDoc */
    SessionStatus["Abnormal"] = "abnormal";
})(SessionStatus || (SessionStatus = {}));
var RequestSessionStatus;
(function (RequestSessionStatus) {
    /** JSDoc */
    RequestSessionStatus["Ok"] = "ok";
    /** JSDoc */
    RequestSessionStatus["Errored"] = "errored";
    /** JSDoc */
    RequestSessionStatus["Crashed"] = "crashed";
})(RequestSessionStatus || (RequestSessionStatus = {}));

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Checks whether given value's type is an object literal
 * {@link isPlainObject}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
function isPlainObject(wat) {
    return Object.prototype.toString.call(wat) === '[object Object]';
}
/**
 * Checks whether given value has a then function.
 * @param wat A value to be checked.
 */
function isThenable(wat) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return Boolean(wat && wat.then && typeof wat.then === 'function');
}

/**
 * Checks whether we're in the Node.js or Browser environment
 *
 * @returns Answer to given question
 */
function isNodeEnv() {
    return Object.prototype.toString.call(typeof process !== 'undefined' ? process : 0) === '[object process]';
}
/**
 * Requires a module which is protected against bundler minification.
 *
 * @param request The module path to resolve
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
function dynamicRequire(mod, request) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return mod.require(request);
}

var fallbackGlobalObject = {};
/**
 * Safely get global scope object
 *
 * @returns Global scope object
 */
function getGlobalObject() {
    return (isNodeEnv()
        ? global
        : typeof window !== 'undefined'
            ? window
            : typeof self !== 'undefined'
                ? self
                : fallbackGlobalObject);
}
/**
 * UUID4 generator
 *
 * @returns string Generated UUID4.
 */
function uuid4() {
    var global = getGlobalObject();
    var crypto = global.crypto || global.msCrypto;
    if (!(crypto === void 0) && crypto.getRandomValues) {
        // Use window.crypto API if available
        var arr = new Uint16Array(8);
        crypto.getRandomValues(arr);
        // set 4 in byte 7
        // eslint-disable-next-line no-bitwise
        arr[3] = (arr[3] & 0xfff) | 0x4000;
        // set 2 most significant bits of byte 9 to '10'
        // eslint-disable-next-line no-bitwise
        arr[4] = (arr[4] & 0x3fff) | 0x8000;
        var pad = function (num) {
            var v = num.toString(16);
            while (v.length < 4) {
                v = "0" + v;
            }
            return v;
        };
        return (pad(arr[0]) + pad(arr[1]) + pad(arr[2]) + pad(arr[3]) + pad(arr[4]) + pad(arr[5]) + pad(arr[6]) + pad(arr[7]));
    }
    // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/2117523#2117523
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        // eslint-disable-next-line no-bitwise
        var r = (Math.random() * 16) | 0;
        // eslint-disable-next-line no-bitwise
        var v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
/** JSDoc */
function consoleSandbox(callback) {
    var global = getGlobalObject();
    var levels = ['debug', 'info', 'warn', 'error', 'log', 'assert'];
    if (!('console' in global)) {
        return callback();
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    var originalConsole = global.console;
    var wrappedLevels = {};
    // Restore all wrapped console methods
    levels.forEach(function (level) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (level in global.console && originalConsole[level].__sentry_original__) {
            wrappedLevels[level] = originalConsole[level];
            originalConsole[level] = originalConsole[level].__sentry_original__;
        }
    });
    // Perform callback manipulations
    var result = callback();
    // Revert restoration to wrapped state
    Object.keys(wrappedLevels).forEach(function (level) {
        originalConsole[level] = wrappedLevels[level];
    });
    return result;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: Implement different loggers for different environments
var global$1 = getGlobalObject();
/** Prefix for logging strings */
var PREFIX = 'Sentry Logger ';
/** JSDoc */
var Logger = /** @class */ (function () {
    /** JSDoc */
    function Logger() {
        this._enabled = false;
    }
    /** JSDoc */
    Logger.prototype.disable = function () {
        this._enabled = false;
    };
    /** JSDoc */
    Logger.prototype.enable = function () {
        this._enabled = true;
    };
    /** JSDoc */
    Logger.prototype.log = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!this._enabled) {
            return;
        }
        consoleSandbox(function () {
            global$1.console.log(PREFIX + "[Log]: " + args.join(' '));
        });
    };
    /** JSDoc */
    Logger.prototype.warn = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!this._enabled) {
            return;
        }
        consoleSandbox(function () {
            global$1.console.warn(PREFIX + "[Warn]: " + args.join(' '));
        });
    };
    /** JSDoc */
    Logger.prototype.error = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!this._enabled) {
            return;
        }
        consoleSandbox(function () {
            global$1.console.error(PREFIX + "[Error]: " + args.join(' '));
        });
    };
    return Logger;
}());
// Ensure we only have a single logger instance, even if multiple versions of @sentry/utils are being used
global$1.__SENTRY__ = global$1.__SENTRY__ || {};
var logger = global$1.__SENTRY__.logger || (global$1.__SENTRY__.logger = new Logger());

/**
 * Given any object, return the new object with removed keys that value was `undefined`.
 * Works recursively on objects and arrays.
 */
function dropUndefinedKeys(val) {
    var e_1, _a;
    if (isPlainObject(val)) {
        var obj = val;
        var rv = {};
        try {
            for (var _b = __values(Object.keys(obj)), _c = _b.next(); !_c.done; _c = _b.next()) {
                var key = _c.value;
                if (typeof obj[key] !== 'undefined') {
                    rv[key] = dropUndefinedKeys(obj[key]);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return rv;
    }
    if (Array.isArray(val)) {
        return val.map(dropUndefinedKeys);
    }
    return val;
}

/* eslint-disable @typescript-eslint/explicit-function-return-type */
/** SyncPromise internal states */
var States;
(function (States) {
    /** Pending */
    States["PENDING"] = "PENDING";
    /** Resolved / OK */
    States["RESOLVED"] = "RESOLVED";
    /** Rejected / Error */
    States["REJECTED"] = "REJECTED";
})(States || (States = {}));
/**
 * Thenable class that behaves like a Promise and follows it's interface
 * but is not async internally
 */
var SyncPromise = /** @class */ (function () {
    function SyncPromise(executor) {
        var _this = this;
        this._state = States.PENDING;
        this._handlers = [];
        /** JSDoc */
        this._resolve = function (value) {
            _this._setResult(States.RESOLVED, value);
        };
        /** JSDoc */
        this._reject = function (reason) {
            _this._setResult(States.REJECTED, reason);
        };
        /** JSDoc */
        this._setResult = function (state, value) {
            if (_this._state !== States.PENDING) {
                return;
            }
            if (isThenable(value)) {
                void value.then(_this._resolve, _this._reject);
                return;
            }
            _this._state = state;
            _this._value = value;
            _this._executeHandlers();
        };
        // TODO: FIXME
        /** JSDoc */
        this._attachHandler = function (handler) {
            _this._handlers = _this._handlers.concat(handler);
            _this._executeHandlers();
        };
        /** JSDoc */
        this._executeHandlers = function () {
            if (_this._state === States.PENDING) {
                return;
            }
            var cachedHandlers = _this._handlers.slice();
            _this._handlers = [];
            cachedHandlers.forEach(function (handler) {
                if (handler.done) {
                    return;
                }
                if (_this._state === States.RESOLVED) {
                    if (handler.onfulfilled) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        handler.onfulfilled(_this._value);
                    }
                }
                if (_this._state === States.REJECTED) {
                    if (handler.onrejected) {
                        handler.onrejected(_this._value);
                    }
                }
                handler.done = true;
            });
        };
        try {
            executor(this._resolve, this._reject);
        }
        catch (e) {
            this._reject(e);
        }
    }
    /** JSDoc */
    SyncPromise.resolve = function (value) {
        return new SyncPromise(function (resolve) {
            resolve(value);
        });
    };
    /** JSDoc */
    SyncPromise.reject = function (reason) {
        return new SyncPromise(function (_, reject) {
            reject(reason);
        });
    };
    /** JSDoc */
    SyncPromise.all = function (collection) {
        return new SyncPromise(function (resolve, reject) {
            if (!Array.isArray(collection)) {
                reject(new TypeError("Promise.all requires an array as input."));
                return;
            }
            if (collection.length === 0) {
                resolve([]);
                return;
            }
            var counter = collection.length;
            var resolvedCollection = [];
            collection.forEach(function (item, index) {
                void SyncPromise.resolve(item)
                    .then(function (value) {
                    resolvedCollection[index] = value;
                    counter -= 1;
                    if (counter !== 0) {
                        return;
                    }
                    resolve(resolvedCollection);
                })
                    .then(null, reject);
            });
        });
    };
    /** JSDoc */
    SyncPromise.prototype.then = function (onfulfilled, onrejected) {
        var _this = this;
        return new SyncPromise(function (resolve, reject) {
            _this._attachHandler({
                done: false,
                onfulfilled: function (result) {
                    if (!onfulfilled) {
                        // TODO: ¯\_(ツ)_/¯
                        // TODO: FIXME
                        resolve(result);
                        return;
                    }
                    try {
                        resolve(onfulfilled(result));
                        return;
                    }
                    catch (e) {
                        reject(e);
                        return;
                    }
                },
                onrejected: function (reason) {
                    if (!onrejected) {
                        reject(reason);
                        return;
                    }
                    try {
                        resolve(onrejected(reason));
                        return;
                    }
                    catch (e) {
                        reject(e);
                        return;
                    }
                },
            });
        });
    };
    /** JSDoc */
    SyncPromise.prototype.catch = function (onrejected) {
        return this.then(function (val) { return val; }, onrejected);
    };
    /** JSDoc */
    SyncPromise.prototype.finally = function (onfinally) {
        var _this = this;
        return new SyncPromise(function (resolve, reject) {
            var val;
            var isRejected;
            return _this.then(function (value) {
                isRejected = false;
                val = value;
                if (onfinally) {
                    onfinally();
                }
            }, function (reason) {
                isRejected = true;
                val = reason;
                if (onfinally) {
                    onfinally();
                }
            }).then(function () {
                if (isRejected) {
                    reject(val);
                    return;
                }
                resolve(val);
            });
        });
    };
    /** JSDoc */
    SyncPromise.prototype.toString = function () {
        return '[object SyncPromise]';
    };
    return SyncPromise;
}());

/**
 * A TimestampSource implementation for environments that do not support the Performance Web API natively.
 *
 * Note that this TimestampSource does not use a monotonic clock. A call to `nowSeconds` may return a timestamp earlier
 * than a previously returned value. We do not try to emulate a monotonic behavior in order to facilitate debugging. It
 * is more obvious to explain "why does my span have negative duration" than "why my spans have zero duration".
 */
var dateTimestampSource = {
    nowSeconds: function () { return Date.now() / 1000; },
};
/**
 * Returns a wrapper around the native Performance API browser implementation, or undefined for browsers that do not
 * support the API.
 *
 * Wrapping the native API works around differences in behavior from different browsers.
 */
function getBrowserPerformance() {
    var performance = getGlobalObject().performance;
    if (!performance || !performance.now) {
        return undefined;
    }
    // Replace performance.timeOrigin with our own timeOrigin based on Date.now().
    //
    // This is a partial workaround for browsers reporting performance.timeOrigin such that performance.timeOrigin +
    // performance.now() gives a date arbitrarily in the past.
    //
    // Additionally, computing timeOrigin in this way fills the gap for browsers where performance.timeOrigin is
    // undefined.
    //
    // The assumption that performance.timeOrigin + performance.now() ~= Date.now() is flawed, but we depend on it to
    // interact with data coming out of performance entries.
    //
    // Note that despite recommendations against it in the spec, browsers implement the Performance API with a clock that
    // might stop when the computer is asleep (and perhaps under other circumstances). Such behavior causes
    // performance.timeOrigin + performance.now() to have an arbitrary skew over Date.now(). In laptop computers, we have
    // observed skews that can be as long as days, weeks or months.
    //
    // See https://github.com/getsentry/sentry-javascript/issues/2590.
    //
    // BUG: despite our best intentions, this workaround has its limitations. It mostly addresses timings of pageload
    // transactions, but ignores the skew built up over time that can aversely affect timestamps of navigation
    // transactions of long-lived web pages.
    var timeOrigin = Date.now() - performance.now();
    return {
        now: function () { return performance.now(); },
        timeOrigin: timeOrigin,
    };
}
/**
 * Returns the native Performance API implementation from Node.js. Returns undefined in old Node.js versions that don't
 * implement the API.
 */
function getNodePerformance() {
    try {
        var perfHooks = dynamicRequire(module, 'perf_hooks');
        return perfHooks.performance;
    }
    catch (_) {
        return undefined;
    }
}
/**
 * The Performance API implementation for the current platform, if available.
 */
var platformPerformance = isNodeEnv() ? getNodePerformance() : getBrowserPerformance();
var timestampSource = platformPerformance === undefined
    ? dateTimestampSource
    : {
        nowSeconds: function () { return (platformPerformance.timeOrigin + platformPerformance.now()) / 1000; },
    };
/**
 * Returns a timestamp in seconds since the UNIX epoch using the Date API.
 */
var dateTimestampInSeconds = dateTimestampSource.nowSeconds.bind(dateTimestampSource);
/**
 * Returns a timestamp in seconds since the UNIX epoch using either the Performance or Date APIs, depending on the
 * availability of the Performance API.
 *
 * See `usingPerformanceAPI` to test whether the Performance API is used.
 *
 * BUG: Note that because of how browsers implement the Performance API, the clock might stop when the computer is
 * asleep. This creates a skew between `dateTimestampInSeconds` and `timestampInSeconds`. The
 * skew can grow to arbitrary amounts like days, weeks or months.
 * See https://github.com/getsentry/sentry-javascript/issues/2590.
 */
var timestampInSeconds = timestampSource.nowSeconds.bind(timestampSource);
/**
 * The number of milliseconds since the UNIX epoch. This value is only usable in a browser, and only when the
 * performance API is available.
 */
var browserPerformanceTimeOrigin = (function () {
    // Unfortunately browsers may report an inaccurate time origin data, through either performance.timeOrigin or
    // performance.timing.navigationStart, which results in poor results in performance data. We only treat time origin
    // data as reliable if they are within a reasonable threshold of the current time.
    var performance = getGlobalObject().performance;
    if (!performance || !performance.now) {
        return undefined;
    }
    var threshold = 3600 * 1000;
    var performanceNow = performance.now();
    var dateNow = Date.now();
    // if timeOrigin isn't available set delta to threshold so it isn't used
    var timeOriginDelta = performance.timeOrigin
        ? Math.abs(performance.timeOrigin + performanceNow - dateNow)
        : threshold;
    var timeOriginIsReliable = timeOriginDelta < threshold;
    // While performance.timing.navigationStart is deprecated in favor of performance.timeOrigin, performance.timeOrigin
    // is not as widely supported. Namely, performance.timeOrigin is undefined in Safari as of writing.
    // Also as of writing, performance.timing is not available in Web Workers in mainstream browsers, so it is not always
    // a valid fallback. In the absence of an initial time provided by the browser, fallback to the current time from the
    // Date API.
    // eslint-disable-next-line deprecation/deprecation
    var navigationStart = performance.timing && performance.timing.navigationStart;
    var hasNavigationStart = typeof navigationStart === 'number';
    // if navigationStart isn't available set delta to threshold so it isn't used
    var navigationStartDelta = hasNavigationStart ? Math.abs(navigationStart + performanceNow - dateNow) : threshold;
    var navigationStartIsReliable = navigationStartDelta < threshold;
    if (timeOriginIsReliable || navigationStartIsReliable) {
        // Use the more reliable time origin
        if (timeOriginDelta <= navigationStartDelta) {
            return performance.timeOrigin;
        }
        else {
            return navigationStart;
        }
    }
    return dateNow;
})();

/**
 * Absolute maximum number of breadcrumbs added to an event.
 * The `maxBreadcrumbs` option cannot be higher than this value.
 */
var MAX_BREADCRUMBS = 100;
/**
 * Holds additional event information. {@link Scope.applyToEvent} will be
 * called by the client before an event will be sent.
 */
var Scope = /** @class */ (function () {
    function Scope() {
        /** Flag if notifiying is happening. */
        this._notifyingListeners = false;
        /** Callback for client to receive scope changes. */
        this._scopeListeners = [];
        /** Callback list that will be called after {@link applyToEvent}. */
        this._eventProcessors = [];
        /** Array of breadcrumbs. */
        this._breadcrumbs = [];
        /** User */
        this._user = {};
        /** Tags */
        this._tags = {};
        /** Extra */
        this._extra = {};
        /** Contexts */
        this._contexts = {};
    }
    /**
     * Inherit values from the parent scope.
     * @param scope to clone.
     */
    Scope.clone = function (scope) {
        var newScope = new Scope();
        if (scope) {
            newScope._breadcrumbs = __spread(scope._breadcrumbs);
            newScope._tags = __assign({}, scope._tags);
            newScope._extra = __assign({}, scope._extra);
            newScope._contexts = __assign({}, scope._contexts);
            newScope._user = scope._user;
            newScope._level = scope._level;
            newScope._span = scope._span;
            newScope._session = scope._session;
            newScope._transactionName = scope._transactionName;
            newScope._fingerprint = scope._fingerprint;
            newScope._eventProcessors = __spread(scope._eventProcessors);
            newScope._requestSession = scope._requestSession;
        }
        return newScope;
    };
    /**
     * Add internal on change listener. Used for sub SDKs that need to store the scope.
     * @hidden
     */
    Scope.prototype.addScopeListener = function (callback) {
        this._scopeListeners.push(callback);
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.addEventProcessor = function (callback) {
        this._eventProcessors.push(callback);
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setUser = function (user) {
        this._user = user || {};
        if (this._session) {
            this._session.update({ user: user });
        }
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.getUser = function () {
        return this._user;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.getRequestSession = function () {
        return this._requestSession;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setRequestSession = function (requestSession) {
        this._requestSession = requestSession;
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setTags = function (tags) {
        this._tags = __assign(__assign({}, this._tags), tags);
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setTag = function (key, value) {
        var _a;
        this._tags = __assign(__assign({}, this._tags), (_a = {}, _a[key] = value, _a));
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setExtras = function (extras) {
        this._extra = __assign(__assign({}, this._extra), extras);
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setExtra = function (key, extra) {
        var _a;
        this._extra = __assign(__assign({}, this._extra), (_a = {}, _a[key] = extra, _a));
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setFingerprint = function (fingerprint) {
        this._fingerprint = fingerprint;
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setLevel = function (level) {
        this._level = level;
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setTransactionName = function (name) {
        this._transactionName = name;
        this._notifyScopeListeners();
        return this;
    };
    /**
     * Can be removed in major version.
     * @deprecated in favor of {@link this.setTransactionName}
     */
    Scope.prototype.setTransaction = function (name) {
        return this.setTransactionName(name);
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setContext = function (key, context) {
        var _a;
        if (context === null) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this._contexts[key];
        }
        else {
            this._contexts = __assign(__assign({}, this._contexts), (_a = {}, _a[key] = context, _a));
        }
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setSpan = function (span) {
        this._span = span;
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.getSpan = function () {
        return this._span;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.getTransaction = function () {
        var _a, _b, _c, _d;
        // often, this span will be a transaction, but it's not guaranteed to be
        var span = this.getSpan();
        // try it the new way first
        if ((_a = span) === null || _a === void 0 ? void 0 : _a.transaction) {
            return (_b = span) === null || _b === void 0 ? void 0 : _b.transaction;
        }
        // fallback to the old way (known bug: this only finds transactions with sampled = true)
        if ((_d = (_c = span) === null || _c === void 0 ? void 0 : _c.spanRecorder) === null || _d === void 0 ? void 0 : _d.spans[0]) {
            return span.spanRecorder.spans[0];
        }
        // neither way found a transaction
        return undefined;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.setSession = function (session) {
        if (!session) {
            delete this._session;
        }
        else {
            this._session = session;
        }
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.getSession = function () {
        return this._session;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.update = function (captureContext) {
        if (!captureContext) {
            return this;
        }
        if (typeof captureContext === 'function') {
            var updatedScope = captureContext(this);
            return updatedScope instanceof Scope ? updatedScope : this;
        }
        if (captureContext instanceof Scope) {
            this._tags = __assign(__assign({}, this._tags), captureContext._tags);
            this._extra = __assign(__assign({}, this._extra), captureContext._extra);
            this._contexts = __assign(__assign({}, this._contexts), captureContext._contexts);
            if (captureContext._user && Object.keys(captureContext._user).length) {
                this._user = captureContext._user;
            }
            if (captureContext._level) {
                this._level = captureContext._level;
            }
            if (captureContext._fingerprint) {
                this._fingerprint = captureContext._fingerprint;
            }
            if (captureContext._requestSession) {
                this._requestSession = captureContext._requestSession;
            }
        }
        else if (isPlainObject(captureContext)) {
            // eslint-disable-next-line no-param-reassign
            captureContext = captureContext;
            this._tags = __assign(__assign({}, this._tags), captureContext.tags);
            this._extra = __assign(__assign({}, this._extra), captureContext.extra);
            this._contexts = __assign(__assign({}, this._contexts), captureContext.contexts);
            if (captureContext.user) {
                this._user = captureContext.user;
            }
            if (captureContext.level) {
                this._level = captureContext.level;
            }
            if (captureContext.fingerprint) {
                this._fingerprint = captureContext.fingerprint;
            }
            if (captureContext.requestSession) {
                this._requestSession = captureContext.requestSession;
            }
        }
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.clear = function () {
        this._breadcrumbs = [];
        this._tags = {};
        this._extra = {};
        this._user = {};
        this._contexts = {};
        this._level = undefined;
        this._transactionName = undefined;
        this._fingerprint = undefined;
        this._requestSession = undefined;
        this._span = undefined;
        this._session = undefined;
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.addBreadcrumb = function (breadcrumb, maxBreadcrumbs) {
        var maxCrumbs = typeof maxBreadcrumbs === 'number' ? Math.min(maxBreadcrumbs, MAX_BREADCRUMBS) : MAX_BREADCRUMBS;
        // No data has been changed, so don't notify scope listeners
        if (maxCrumbs <= 0) {
            return this;
        }
        var mergedBreadcrumb = __assign({ timestamp: dateTimestampInSeconds() }, breadcrumb);
        this._breadcrumbs = __spread(this._breadcrumbs, [mergedBreadcrumb]).slice(-maxCrumbs);
        this._notifyScopeListeners();
        return this;
    };
    /**
     * @inheritDoc
     */
    Scope.prototype.clearBreadcrumbs = function () {
        this._breadcrumbs = [];
        this._notifyScopeListeners();
        return this;
    };
    /**
     * Applies the current context and fingerprint to the event.
     * Note that breadcrumbs will be added by the client.
     * Also if the event has already breadcrumbs on it, we do not merge them.
     * @param event Event
     * @param hint May contain additional informartion about the original exception.
     * @hidden
     */
    Scope.prototype.applyToEvent = function (event, hint) {
        var _a;
        if (this._extra && Object.keys(this._extra).length) {
            event.extra = __assign(__assign({}, this._extra), event.extra);
        }
        if (this._tags && Object.keys(this._tags).length) {
            event.tags = __assign(__assign({}, this._tags), event.tags);
        }
        if (this._user && Object.keys(this._user).length) {
            event.user = __assign(__assign({}, this._user), event.user);
        }
        if (this._contexts && Object.keys(this._contexts).length) {
            event.contexts = __assign(__assign({}, this._contexts), event.contexts);
        }
        if (this._level) {
            event.level = this._level;
        }
        if (this._transactionName) {
            event.transaction = this._transactionName;
        }
        // We want to set the trace context for normal events only if there isn't already
        // a trace context on the event. There is a product feature in place where we link
        // errors with transaction and it relys on that.
        if (this._span) {
            event.contexts = __assign({ trace: this._span.getTraceContext() }, event.contexts);
            var transactionName = (_a = this._span.transaction) === null || _a === void 0 ? void 0 : _a.name;
            if (transactionName) {
                event.tags = __assign({ transaction: transactionName }, event.tags);
            }
        }
        this._applyFingerprint(event);
        event.breadcrumbs = __spread((event.breadcrumbs || []), this._breadcrumbs);
        event.breadcrumbs = event.breadcrumbs.length > 0 ? event.breadcrumbs : undefined;
        return this._notifyEventProcessors(__spread(getGlobalEventProcessors(), this._eventProcessors), event, hint);
    };
    /**
     * This will be called after {@link applyToEvent} is finished.
     */
    Scope.prototype._notifyEventProcessors = function (processors, event, hint, index) {
        var _this = this;
        if (index === void 0) { index = 0; }
        return new SyncPromise(function (resolve, reject) {
            var processor = processors[index];
            if (event === null || typeof processor !== 'function') {
                resolve(event);
            }
            else {
                var result = processor(__assign({}, event), hint);
                if (isThenable(result)) {
                    void result
                        .then(function (final) { return _this._notifyEventProcessors(processors, final, hint, index + 1).then(resolve); })
                        .then(null, reject);
                }
                else {
                    void _this._notifyEventProcessors(processors, result, hint, index + 1)
                        .then(resolve)
                        .then(null, reject);
                }
            }
        });
    };
    /**
     * This will be called on every set call.
     */
    Scope.prototype._notifyScopeListeners = function () {
        var _this = this;
        // We need this check for this._notifyingListeners to be able to work on scope during updates
        // If this check is not here we'll produce endless recursion when something is done with the scope
        // during the callback.
        if (!this._notifyingListeners) {
            this._notifyingListeners = true;
            this._scopeListeners.forEach(function (callback) {
                callback(_this);
            });
            this._notifyingListeners = false;
        }
    };
    /**
     * Applies fingerprint from the scope to the event if there's one,
     * uses message if there's one instead or get rid of empty fingerprint
     */
    Scope.prototype._applyFingerprint = function (event) {
        // Make sure it's an array first and we actually have something in place
        event.fingerprint = event.fingerprint
            ? Array.isArray(event.fingerprint)
                ? event.fingerprint
                : [event.fingerprint]
            : [];
        // If we have something on the scope, then merge it with event
        if (this._fingerprint) {
            event.fingerprint = event.fingerprint.concat(this._fingerprint);
        }
        // If we have no data at all, remove empty array default
        if (event.fingerprint && !event.fingerprint.length) {
            delete event.fingerprint;
        }
    };
    return Scope;
}());
/**
 * Retruns the global event processors.
 */
function getGlobalEventProcessors() {
    /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access  */
    var global = getGlobalObject();
    global.__SENTRY__ = global.__SENTRY__ || {};
    global.__SENTRY__.globalEventProcessors = global.__SENTRY__.globalEventProcessors || [];
    return global.__SENTRY__.globalEventProcessors;
    /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */
}

/**
 * @inheritdoc
 */
var Session = /** @class */ (function () {
    function Session(context) {
        this.errors = 0;
        this.sid = uuid4();
        this.duration = 0;
        this.status = SessionStatus.Ok;
        this.init = true;
        this.ignoreDuration = false;
        // Both timestamp and started are in seconds since the UNIX epoch.
        var startingTime = timestampInSeconds();
        this.timestamp = startingTime;
        this.started = startingTime;
        if (context) {
            this.update(context);
        }
    }
    /** JSDoc */
    // eslint-disable-next-line complexity
    Session.prototype.update = function (context) {
        if (context === void 0) { context = {}; }
        if (context.user) {
            if (!this.ipAddress && context.user.ip_address) {
                this.ipAddress = context.user.ip_address;
            }
            if (!this.did && !context.did) {
                this.did = context.user.id || context.user.email || context.user.username;
            }
        }
        this.timestamp = context.timestamp || timestampInSeconds();
        if (context.ignoreDuration) {
            this.ignoreDuration = context.ignoreDuration;
        }
        if (context.sid) {
            // Good enough uuid validation. — Kamil
            this.sid = context.sid.length === 32 ? context.sid : uuid4();
        }
        if (context.init !== undefined) {
            this.init = context.init;
        }
        if (!this.did && context.did) {
            this.did = "" + context.did;
        }
        if (typeof context.started === 'number') {
            this.started = context.started;
        }
        if (this.ignoreDuration) {
            this.duration = undefined;
        }
        else if (typeof context.duration === 'number') {
            this.duration = context.duration;
        }
        else {
            var duration = this.timestamp - this.started;
            this.duration = duration >= 0 ? duration : 0;
        }
        if (context.release) {
            this.release = context.release;
        }
        if (context.environment) {
            this.environment = context.environment;
        }
        if (!this.ipAddress && context.ipAddress) {
            this.ipAddress = context.ipAddress;
        }
        if (!this.userAgent && context.userAgent) {
            this.userAgent = context.userAgent;
        }
        if (typeof context.errors === 'number') {
            this.errors = context.errors;
        }
        if (context.status) {
            this.status = context.status;
        }
    };
    /** JSDoc */
    Session.prototype.close = function (status) {
        if (status) {
            this.update({ status: status });
        }
        else if (this.status === SessionStatus.Ok) {
            this.update({ status: SessionStatus.Exited });
        }
        else {
            this.update();
        }
    };
    /** JSDoc */
    Session.prototype.toJSON = function () {
        return dropUndefinedKeys({
            sid: "" + this.sid,
            init: this.init,
            // Make sure that sec is converted to ms for date constructor
            started: new Date(this.started * 1000).toISOString(),
            timestamp: new Date(this.timestamp * 1000).toISOString(),
            status: this.status,
            errors: this.errors,
            did: typeof this.did === 'number' || typeof this.did === 'string' ? "" + this.did : undefined,
            duration: this.duration,
            attrs: dropUndefinedKeys({
                release: this.release,
                environment: this.environment,
                ip_address: this.ipAddress,
                user_agent: this.userAgent,
            }),
        });
    };
    return Session;
}());

/**
 * API compatibility version of this hub.
 *
 * WARNING: This number should only be increased when the global interface
 * changes and new methods are introduced.
 *
 * @hidden
 */
var API_VERSION = 4;
/**
 * Default maximum number of breadcrumbs added to an event. Can be overwritten
 * with {@link Options.maxBreadcrumbs}.
 */
var DEFAULT_BREADCRUMBS = 100;
/**
 * @inheritDoc
 */
var Hub = /** @class */ (function () {
    /**
     * Creates a new instance of the hub, will push one {@link Layer} into the
     * internal stack on creation.
     *
     * @param client bound to the hub.
     * @param scope bound to the hub.
     * @param version number, higher number means higher priority.
     */
    function Hub(client, scope, _version) {
        if (scope === void 0) { scope = new Scope(); }
        if (_version === void 0) { _version = API_VERSION; }
        this._version = _version;
        /** Is a {@link Layer}[] containing the client and scope */
        this._stack = [{}];
        this.getStackTop().scope = scope;
        this.bindClient(client);
    }
    /**
     * @inheritDoc
     */
    Hub.prototype.isOlderThan = function (version) {
        return this._version < version;
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.bindClient = function (client) {
        var top = this.getStackTop();
        top.client = client;
        if (client && client.setupIntegrations) {
            client.setupIntegrations();
        }
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.pushScope = function () {
        // We want to clone the content of prev scope
        var scope = Scope.clone(this.getScope());
        this.getStack().push({
            client: this.getClient(),
            scope: scope,
        });
        return scope;
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.popScope = function () {
        if (this.getStack().length <= 1)
            return false;
        return !!this.getStack().pop();
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.withScope = function (callback) {
        var scope = this.pushScope();
        try {
            callback(scope);
        }
        finally {
            this.popScope();
        }
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.getClient = function () {
        return this.getStackTop().client;
    };
    /** Returns the scope of the top stack. */
    Hub.prototype.getScope = function () {
        return this.getStackTop().scope;
    };
    /** Returns the scope stack for domains or the process. */
    Hub.prototype.getStack = function () {
        return this._stack;
    };
    /** Returns the topmost scope layer in the order domain > local > process. */
    Hub.prototype.getStackTop = function () {
        return this._stack[this._stack.length - 1];
    };
    /**
     * @inheritDoc
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    Hub.prototype.captureException = function (exception, hint) {
        var eventId = (this._lastEventId = uuid4());
        var finalHint = hint;
        // If there's no explicit hint provided, mimick the same thing that would happen
        // in the minimal itself to create a consistent behavior.
        // We don't do this in the client, as it's the lowest level API, and doing this,
        // would prevent user from having full control over direct calls.
        if (!hint) {
            var syntheticException = void 0;
            try {
                throw new Error('Sentry syntheticException');
            }
            catch (exception) {
                syntheticException = exception;
            }
            finalHint = {
                originalException: exception,
                syntheticException: syntheticException,
            };
        }
        this._invokeClient('captureException', exception, __assign(__assign({}, finalHint), { event_id: eventId }));
        return eventId;
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.captureMessage = function (message, level, hint) {
        var eventId = (this._lastEventId = uuid4());
        var finalHint = hint;
        // If there's no explicit hint provided, mimick the same thing that would happen
        // in the minimal itself to create a consistent behavior.
        // We don't do this in the client, as it's the lowest level API, and doing this,
        // would prevent user from having full control over direct calls.
        if (!hint) {
            var syntheticException = void 0;
            try {
                throw new Error(message);
            }
            catch (exception) {
                syntheticException = exception;
            }
            finalHint = {
                originalException: message,
                syntheticException: syntheticException,
            };
        }
        this._invokeClient('captureMessage', message, level, __assign(__assign({}, finalHint), { event_id: eventId }));
        return eventId;
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.captureEvent = function (event, hint) {
        var eventId = (this._lastEventId = uuid4());
        this._invokeClient('captureEvent', event, __assign(__assign({}, hint), { event_id: eventId }));
        return eventId;
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.lastEventId = function () {
        return this._lastEventId;
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.addBreadcrumb = function (breadcrumb, hint) {
        var _a = this.getStackTop(), scope = _a.scope, client = _a.client;
        if (!scope || !client)
            return;
        // eslint-disable-next-line @typescript-eslint/unbound-method
        var _b = (client.getOptions && client.getOptions()) || {}, _c = _b.beforeBreadcrumb, beforeBreadcrumb = _c === void 0 ? null : _c, _d = _b.maxBreadcrumbs, maxBreadcrumbs = _d === void 0 ? DEFAULT_BREADCRUMBS : _d;
        if (maxBreadcrumbs <= 0)
            return;
        var timestamp = dateTimestampInSeconds();
        var mergedBreadcrumb = __assign({ timestamp: timestamp }, breadcrumb);
        var finalBreadcrumb = beforeBreadcrumb
            ? consoleSandbox(function () { return beforeBreadcrumb(mergedBreadcrumb, hint); })
            : mergedBreadcrumb;
        if (finalBreadcrumb === null)
            return;
        scope.addBreadcrumb(finalBreadcrumb, maxBreadcrumbs);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.setUser = function (user) {
        var scope = this.getScope();
        if (scope)
            scope.setUser(user);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.setTags = function (tags) {
        var scope = this.getScope();
        if (scope)
            scope.setTags(tags);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.setExtras = function (extras) {
        var scope = this.getScope();
        if (scope)
            scope.setExtras(extras);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.setTag = function (key, value) {
        var scope = this.getScope();
        if (scope)
            scope.setTag(key, value);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.setExtra = function (key, extra) {
        var scope = this.getScope();
        if (scope)
            scope.setExtra(key, extra);
    };
    /**
     * @inheritDoc
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Hub.prototype.setContext = function (name, context) {
        var scope = this.getScope();
        if (scope)
            scope.setContext(name, context);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.configureScope = function (callback) {
        var _a = this.getStackTop(), scope = _a.scope, client = _a.client;
        if (scope && client) {
            callback(scope);
        }
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.run = function (callback) {
        var oldHub = makeMain(this);
        try {
            callback(this);
        }
        finally {
            makeMain(oldHub);
        }
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.getIntegration = function (integration) {
        var client = this.getClient();
        if (!client)
            return null;
        try {
            return client.getIntegration(integration);
        }
        catch (_oO) {
            logger.warn("Cannot retrieve integration " + integration.id + " from the current Hub");
            return null;
        }
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.startSpan = function (context) {
        return this._callExtensionMethod('startSpan', context);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.startTransaction = function (context, customSamplingContext) {
        return this._callExtensionMethod('startTransaction', context, customSamplingContext);
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.traceHeaders = function () {
        return this._callExtensionMethod('traceHeaders');
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.captureSession = function (endSession) {
        if (endSession === void 0) { endSession = false; }
        // both send the update and pull the session from the scope
        if (endSession) {
            return this.endSession();
        }
        // only send the update
        this._sendSessionUpdate();
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.endSession = function () {
        var _a, _b, _c, _d, _e;
        (_c = (_b = (_a = this.getStackTop()) === null || _a === void 0 ? void 0 : _a.scope) === null || _b === void 0 ? void 0 : _b.getSession()) === null || _c === void 0 ? void 0 : _c.close();
        this._sendSessionUpdate();
        // the session is over; take it off of the scope
        (_e = (_d = this.getStackTop()) === null || _d === void 0 ? void 0 : _d.scope) === null || _e === void 0 ? void 0 : _e.setSession();
    };
    /**
     * @inheritDoc
     */
    Hub.prototype.startSession = function (context) {
        var _a = this.getStackTop(), scope = _a.scope, client = _a.client;
        var _b = (client && client.getOptions()) || {}, release = _b.release, environment = _b.environment;
        // Will fetch userAgent if called from browser sdk
        var global = getGlobalObject();
        var userAgent = (global.navigator || {}).userAgent;
        var session = new Session(__assign(__assign(__assign({ release: release,
            environment: environment }, (scope && { user: scope.getUser() })), (userAgent && { userAgent: userAgent })), context));
        if (scope) {
            // End existing session if there's one
            var currentSession = scope.getSession && scope.getSession();
            if (currentSession && currentSession.status === SessionStatus.Ok) {
                currentSession.update({ status: SessionStatus.Exited });
            }
            this.endSession();
            // Afterwards we set the new session on the scope
            scope.setSession(session);
        }
        return session;
    };
    /**
     * Sends the current Session on the scope
     */
    Hub.prototype._sendSessionUpdate = function () {
        var _a = this.getStackTop(), scope = _a.scope, client = _a.client;
        if (!scope)
            return;
        var session = scope.getSession && scope.getSession();
        if (session) {
            if (client && client.captureSession) {
                client.captureSession(session);
            }
        }
    };
    /**
     * Internal helper function to call a method on the top client if it exists.
     *
     * @param method The method to call on the client.
     * @param args Arguments to pass to the client function.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Hub.prototype._invokeClient = function (method) {
        var _a;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var _b = this.getStackTop(), scope = _b.scope, client = _b.client;
        if (client && client[method]) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
            (_a = client)[method].apply(_a, __spread(args, [scope]));
        }
    };
    /**
     * Calls global extension method and binding current instance to the function call
     */
    // @ts-ignore Function lacks ending return statement and return type does not include 'undefined'. ts(2366)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Hub.prototype._callExtensionMethod = function (method) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var carrier = getMainCarrier();
        var sentry = carrier.__SENTRY__;
        if (sentry && sentry.extensions && typeof sentry.extensions[method] === 'function') {
            return sentry.extensions[method].apply(this, args);
        }
        logger.warn("Extension method " + method + " couldn't be found, doing nothing.");
    };
    return Hub;
}());
/**
 * Returns the global shim registry.
 *
 * FIXME: This function is problematic, because despite always returning a valid Carrier,
 * it has an optional `__SENTRY__` property, which then in turn requires us to always perform an unnecessary check
 * at the call-site. We always access the carrier through this function, so we can guarantee that `__SENTRY__` is there.
 **/
function getMainCarrier() {
    var carrier = getGlobalObject();
    carrier.__SENTRY__ = carrier.__SENTRY__ || {
        extensions: {},
        hub: undefined,
    };
    return carrier;
}
/**
 * Replaces the current main hub with the passed one on the global object
 *
 * @returns The old replaced hub
 */
function makeMain(hub) {
    var registry = getMainCarrier();
    var oldHub = getHubFromCarrier(registry);
    setHubOnCarrier(registry, hub);
    return oldHub;
}
/**
 * Returns the default hub instance.
 *
 * If a hub is already registered in the global carrier but this module
 * contains a more recent version, it replaces the registered version.
 * Otherwise, the currently registered hub will be returned.
 */
function getCurrentHub() {
    // Get main carrier (global for every environment)
    var registry = getMainCarrier();
    // If there's no hub, or its an old API, assign a new one
    if (!hasHubOnCarrier(registry) || getHubFromCarrier(registry).isOlderThan(API_VERSION)) {
        setHubOnCarrier(registry, new Hub());
    }
    // Prefer domains over global if they are there (applicable only to Node environment)
    if (isNodeEnv()) {
        return getHubFromActiveDomain(registry);
    }
    // Return hub that lives on a global object
    return getHubFromCarrier(registry);
}
/**
 * Try to read the hub from an active domain, and fallback to the registry if one doesn't exist
 * @returns discovered hub
 */
function getHubFromActiveDomain(registry) {
    var _a, _b, _c;
    try {
        var activeDomain = (_c = (_b = (_a = getMainCarrier().__SENTRY__) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.domain) === null || _c === void 0 ? void 0 : _c.active;
        // If there's no active domain, just return global hub
        if (!activeDomain) {
            return getHubFromCarrier(registry);
        }
        // If there's no hub on current domain, or it's an old API, assign a new one
        if (!hasHubOnCarrier(activeDomain) || getHubFromCarrier(activeDomain).isOlderThan(API_VERSION)) {
            var registryHubTopStack = getHubFromCarrier(registry).getStackTop();
            setHubOnCarrier(activeDomain, new Hub(registryHubTopStack.client, Scope.clone(registryHubTopStack.scope)));
        }
        // Return hub that lives on a domain
        return getHubFromCarrier(activeDomain);
    }
    catch (_Oo) {
        // Return hub that lives on a global object
        return getHubFromCarrier(registry);
    }
}
/**
 * This will tell whether a carrier has a hub on it or not
 * @param carrier object
 */
function hasHubOnCarrier(carrier) {
    return !!(carrier && carrier.__SENTRY__ && carrier.__SENTRY__.hub);
}
/**
 * This will create a new {@link Hub} and add to the passed object on
 * __SENTRY__.hub.
 * @param carrier object
 * @hidden
 */
function getHubFromCarrier(carrier) {
    if (carrier && carrier.__SENTRY__ && carrier.__SENTRY__.hub)
        return carrier.__SENTRY__.hub;
    carrier.__SENTRY__ = carrier.__SENTRY__ || {};
    carrier.__SENTRY__.hub = new Hub();
    return carrier.__SENTRY__.hub;
}
/**
 * This will set passed {@link Hub} on the passed object's __SENTRY__.hub attribute
 * @param carrier object
 * @param hub Hub
 * @returns A boolean indicating success or failure
 */
function setHubOnCarrier(carrier, hub) {
    if (!carrier)
        return false;
    carrier.__SENTRY__ = carrier.__SENTRY__ || {};
    carrier.__SENTRY__.hub = hub;
    return true;
}

/**
 * This calls a function on the current hub.
 * @param method function to call on hub.
 * @param args to pass to function.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function callOnHub(method) {
    var args = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args[_i - 1] = arguments[_i];
    }
    var hub = getCurrentHub();
    if (hub && hub[method]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return hub[method].apply(hub, __spread(args));
    }
    throw new Error("No hub defined or " + method + " was not found on the hub, please open a bug report.");
}
/**
 * Captures an exception event and sends it to Sentry.
 *
 * @param exception An exception-like object.
 * @returns The generated eventId.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
function captureException(exception, captureContext) {
    var syntheticException;
    try {
        throw new Error('Sentry syntheticException');
    }
    catch (exception) {
        syntheticException = exception;
    }
    return callOnHub('captureException', exception, {
        captureContext: captureContext,
        originalException: exception,
        syntheticException: syntheticException,
    });
}

var fetchPublicSettingsValues = settingsReducers.fetchPublicSettingsValues;
var deleteParam = queryParamsReducers.deleteParam;
var fetchMarkdownDocument = cmsReducers.fetchMarkdownDocument;
var cmsLatestDocumentsContentsSelector = cmsSelectors.cmsLatestDocumentsContentsSelector;
var queryParamsSelector = queryParamsSelectors.queryParamsSelector;
var createSettingsValueSelector = settingsSelectors.createSettingsValueSelector;
var FOOTER_DISCLAIMERS_SETTINGS_KEY = 'disclaimers.footer';
var disclaimerConfigsSelector = createSettingsValueSelector(FOOTER_DISCLAIMERS_SETTINGS_KEY);

var useFooterDisclaimers = function useFooterDisclaimers() {
  var dispatch = useDispatch();
  useEffect(function () {
    var fetchDisclaimerSettings = function fetchDisclaimerSettings() {
      return new Promise(function ($return, $error) {
        var $Try_1_Post = function () {
          try {
            return $return();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };

        var $Try_1_Catch = function (err) {
          try {
            captureException(err, {
              extra: {
                settingsKey: FOOTER_DISCLAIMERS_SETTINGS_KEY
              }
            });
            return $Try_1_Post();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };

        try {
          return Promise.resolve(dispatch(fetchPublicSettingsValues(FOOTER_DISCLAIMERS_SETTINGS_KEY))).then(function ($await_3) {
            try {
              return $Try_1_Post();
            } catch ($boundEx) {
              return $Try_1_Catch($boundEx);
            }
          }, $Try_1_Catch);
        } catch (err) {
          $Try_1_Catch(err);
        }
      });
    };

    fetchDisclaimerSettings();
  }, [dispatch]);
  var disclaimerConfigs = useSelector(disclaimerConfigsSelector);
  var allDisclaimerConfigs = useMemo(function () {
    return disclaimerConfigs !== null && disclaimerConfigs !== void 0 ? disclaimerConfigs : [];
  }, [disclaimerConfigs]);
  var queryParams = useSelector(queryParamsSelector);

  var _useState = useState(new Set()),
      _useState2 = _slicedToArray(_useState, 2),
      consumedQueryParamKeys = _useState2[0],
      setConsumedQueryParamKeys = _useState2[1];

  useEffect(function () {
    var unconsumedQueryParamKeys = allDisclaimerConfigs.reduce(function (memo, _ref) {
      var paramName = _ref.paramName,
          paramValue = _ref.paramValue;

      if (!paramName) {
        return memo;
      }

      var currentValue = queryParams === null || queryParams === void 0 ? void 0 : queryParams[paramName];

      if (!currentValue) {
        return memo;
      }

      var hasValueRequirement = !!paramValue;
      var doesNotMatchValue = hasValueRequirement && currentValue !== paramValue;

      if (doesNotMatchValue) {
        return memo;
      }

      return [].concat(_toConsumableArray(memo), [paramName]);
    }, []);
    setConsumedQueryParamKeys(function (oldConsumedQueryParamKeys) {
      return new Set([].concat(_toConsumableArray(oldConsumedQueryParamKeys), _toConsumableArray(unconsumedQueryParamKeys)));
    }); // so that disclaimers we consume do not appear in subsequent sessions unless query params are re-supplied

    unconsumedQueryParamKeys.forEach(function (param) {
      dispatch(deleteParam(param));
    });
  }, [dispatch, queryParams, allDisclaimerConfigs]);
  var disclaimerConfigsToShow = useMemo(function () {
    return allDisclaimerConfigs.filter(function (_ref2) {
      var paramName = _ref2.paramName;
      return !paramName || consumedQueryParamKeys.has(paramName);
    });
  }, [allDisclaimerConfigs, consumedQueryParamKeys]);
  useEffect(function () {
    var fetchDisclaimers = function fetchDisclaimers() {
      return new Promise(function ($return, $error) {
        var disclaimerFetchPromises;
        disclaimerFetchPromises = disclaimerConfigsToShow.map(function (_ref3) {
          return new Promise(function ($return, $error) {
            var disclaimerID;
            disclaimerID = _ref3.disclaimerID;

            var $Try_2_Post = function () {
              try {
                return $return();
              } catch ($boundEx) {
                return $error($boundEx);
              }
            };

            var $Try_2_Catch = function (err) {
              try {
                captureException(err, {
                  extra: {
                    disclaimerID: disclaimerID
                  }
                });
                return $Try_2_Post();
              } catch ($boundEx) {
                return $error($boundEx);
              }
            };

            try {
              return Promise.resolve(dispatch(fetchMarkdownDocument(disclaimerID))).then(function ($await_4) {
                try {
                  return $Try_2_Post();
                } catch ($boundEx) {
                  return $Try_2_Catch($boundEx);
                }
              }, $Try_2_Catch);
            } catch (err) {
              $Try_2_Catch(err);
            }
          });
        });
        return Promise.resolve(Promise.all(disclaimerFetchPromises)).then(function ($await_5) {
          try {
            return $return();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }, $error);
      });
    };

    fetchDisclaimers();
  }, [dispatch, disclaimerConfigsToShow]);
  var cms = useSelector(cmsLatestDocumentsContentsSelector);
  var disclaimers = useMemo(function () {
    return disclaimerConfigsToShow.map(function (_ref4) {
      var disclaimerID = _ref4.disclaimerID;
      return {
        disclaimerID: disclaimerID,
        markdown: cms === null || cms === void 0 ? void 0 : cms[disclaimerID]
      };
    }).filter(function (_ref5) {
      var markdown = _ref5.markdown;
      return !!markdown;
    });
  }, [cms, disclaimerConfigsToShow]);
  return {
    disclaimers: disclaimers
  };
};

var _excluded$5 = ["className"];

var _templateObject$5, _templateObject2$3, _templateObject3$2, _templateObject4$2, _templateObject5$2;
var isPassportSourceSelector = queryParamsSelectors.isPassportSourceSelector;
var passportDisclaimerSelector = cmsSelectors.passportDisclaimerSelector;
var fetchPassportDisclaimer = cmsReducers.fetchPassportDisclaimer;
var StyledFooter = /*#__PURE__*/styled.footer.withConfig({
  componentId: "sc-1hzpcn7-0"
})(["", " width:100%;"], function (_ref) {
  var $hidden = _ref.$hidden;
  return $hidden && css(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteralLoose(["\n            visibility: hidden;\n        "])));
});
var StyledLinkList = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-1"
})(["display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;margin-bottom:", ";", ",", ":visited{padding:", " 0;}", ";"], spaceMixin('lg'), ActiveNavigationLink, ActiveNavigationLink, spaceMixin('xs'), mediaMixin('lg', css(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteralLoose(["\n            flex-direction: row;\n            margin-bottom: ", ";\n            ", " {\n                padding: ", " ", " ", " ", ";\n            }\n        "])), spaceMixin('lg'), ActiveNavigationLink, spaceMixin('xl'), spaceMixin('md'), spaceMixin('md'), spaceMixin('md'))));
var StyledAdDisclaimer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-2"
})(["width:100%;padding-top:", ";padding-bottom:", ";"], spaceMixin('sm'), spaceMixin('md'));
var StyledAnchor = /*#__PURE__*/styled(Anchor).withConfig({
  componentId: "sc-1hzpcn7-3"
})(["", ""], function (props) {
  return typographyMixin(Object.assign({
    fontType: FONT_TYPES.body
  }, props));
});

var getContactItems = function getContactItems(showPassportOffer) {
  return [{
    id: 'phone',
    Icon: IconPhoneOutline,
    element: /*#__PURE__*/jsx(TextButton, {
      as: "a",
      href: constants.ZHL_PHONE_NUMBER_TEL,
      children: showPassportOffer ? constants.ZHL_OFFER_NUMBER : constants.ZHL_PHONE_NUMBER
    })
  }, {
    id: 'address',
    Icon: IconBuildingsOutline,
    element: /*#__PURE__*/jsxs(Paragraph, {
      children: ["10975 El Monte St.", /*#__PURE__*/jsx("br", {}), "Overland Park, KS 66211"]
    })
  }, {
    id: 'contact',
    Icon: IconAskQuestionOutline,
    element: /*#__PURE__*/jsxs(Paragraph, {
      children: ["Submit concerns or questions through our ", /*#__PURE__*/jsx(Anchor, {
        href: "/contact/",
        children: "contact form."
      })]
    })
  }];
};

var ContactItemInnerContent = /*#__PURE__*/styled(Spacer).withConfig({
  componentId: "sc-1hzpcn7-4"
})(["width:100%;"]);
var ContactItem = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-5"
})(["display:flex;flex-direction:column;align-items:center;width:100%;", " & + &{margin-top:", ";}", "{padding-bottom:", ";& + *{width:100%;border-top:1px ", " solid;padding-top:", ";}}", ""], mediaMixin('lg', css(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteralLoose(["\n            flex: 0 0 33%;\n            width: auto;\n        "])))), spaceMixin('md'), Icon, spaceMixin('sm'), token('colors.borderLight'), spaceMixin('sm'), mediaMixin('lg', css(_templateObject4$2 || (_templateObject4$2 = _taggedTemplateLiteralLoose(["\n            ", " {\n                padding-right: ", ";\n            }\n            & + & {\n                ", " {\n                    padding-left: ", ";\n                }\n            }\n            &:last-child {\n                ", " {\n                    padding-right: 0;\n                }\n            }\n        "])), ContactItemInnerContent, spaceMixin('sm'), ContactItemInnerContent, spaceMixin('sm'), ContactItemInnerContent)));
var StyledContactSection = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-6"
})(["padding:", " 0;border-top:1px ", " solid;border-bottom:1px ", " solid;margin-bottom:", ";"], spaceMixin('lg'), token('colors.textDark'), token('colors.textDark'), spaceMixin('md'));
var ContactHeading = /*#__PURE__*/styled(Heading).withConfig({
  componentId: "sc-1hzpcn7-7"
})(["margin-bottom:", ";", ""], spaceMixin('md'), mediaMixin('lg', VisuallyHiddenMixin));

var ContactSection = function ContactSection(_ref2) {
  var showPassportOffer = _ref2.showPassportOffer;
  return /*#__PURE__*/jsxs(StyledContactSection, {
    children: [/*#__PURE__*/jsx(ContactHeading, {
      level: "3",
      children: "Contact Zillow Home Loans"
    }), /*#__PURE__*/jsx(Flex, {
      display: "flex",
      flexDirection: {
        xs: 'column',
        lg: 'row'
      },
      alignItems: "center",
      children: getContactItems(showPassportOffer).map(function (_ref3) {
        var ItemIcon = _ref3.Icon,
            element = _ref3.element,
            id = _ref3.id;
        return /*#__PURE__*/jsx(ContactItem, {
          children: /*#__PURE__*/jsxs(ContactItemInnerContent, {
            children: [/*#__PURE__*/jsx(ItemIcon, {
              size: "lg",
              title: "",
              alt: ""
            }), element]
          })
        }, id);
      })
    })]
  });
};

ContactSection.propTypes = {
  showPassportOffer: PropTypes.bool
};
var StyledNMLS = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-8"
})(["display:flex;flex-direction:column;width:100%;", ";"], mediaMixin('xl', css(_templateObject5$2 || (_templateObject5$2 = _taggedTemplateLiteralLoose(["\n            flex-direction: row;\n            justify-content: space-between;\n        "])))));
var StyledLegalese = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-9"
})(["display:flex;flex-direction:column;align-items:center;max-width:575px;"]);
var StyledEHLLogo = /*#__PURE__*/styled(SvgEhlLogo).withConfig({
  componentId: "sc-1hzpcn7-10"
})(["height:auto;width:80px;fill:", ";"], token('colors.textDark'));
var StyledLegaleseContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-11"
})(["display:flex;flex-direction:column;margin-bottom:", ";", "{margin:", " auto ", " auto;}"], spaceMixin('lg'), StyledEHLLogo, spaceMixin('md'), spaceMixin('md'));
var StyledFooterWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1hzpcn7-12"
})(["max-width:1280px;margin:0 auto;"]);
var StyledFooterContent = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-1hzpcn7-13"
})(["flex-direction:column;align-items:center;text-align:center;padding:", ";"], spaceMixin('md'));

var Footer = function Footer(_ref4) {
  var className = _ref4.className,
      rest = _objectWithoutProperties(_ref4, _excluded$5);

  var _useContext = useContext(PageContext),
      isOffer = _useContext.isOffer;

  var isPassportSource = useSelector(isPassportSourceSelector);
  var showPassportOffer = isOffer && isPassportSource;
  var dispatch = useDispatch();
  useEffect(function () {
    dispatch(fetchPassportDisclaimer());
  }, [dispatch]);
  var passportDisclaimer = useSelector(passportDisclaimerSelector);

  var _useFooterDisclaimers = useFooterDisclaimers(),
      disclaimers = _useFooterDisclaimers.disclaimers;

  return /*#__PURE__*/jsx(StyledFooter, Object.assign({
    className: className
  }, rest, {
    children: /*#__PURE__*/jsxs(StyledFooterWrapper, {
      children: [/*#__PURE__*/jsxs(StyledFooterContent, {
        display: "flex",
        children: [/*#__PURE__*/jsxs(Flex, {
          display: "flex",
          flexDirection: "column",
          children: [/*#__PURE__*/jsxs(StyledLinkList, {
            children: [/*#__PURE__*/jsx(ActiveNavigationLink, {
              href: "https://careers.zillowgroup.com/List-Jobs?type=Mortgages",
              children: "Careers"
            }), /*#__PURE__*/jsx(ActiveNavigationLink, {
              href: "/terms/",
              children: "Terms of use"
            }), /*#__PURE__*/jsx(ActiveNavigationLink, {
              href: "/privacy/",
              children: "Privacy policy"
            }), /*#__PURE__*/jsx(ActiveNavigationLink, {
              href: "/licensing/",
              children: "Licensing info"
            }), setupOneTrustFooter(function (props) {
              return /*#__PURE__*/jsx(ActiveNavigationLink, Object.assign({
                as: "button"
              }, props, {
                children: "Cookie preference"
              }));
            })]
          }), /*#__PURE__*/jsx(ContactSection, {
            showPassportOffer: showPassportOffer
          })]
        }), /*#__PURE__*/jsxs(StyledLegaleseContainer, {
          children: [/*#__PURE__*/jsx(StyledEHLLogo, {
            "aria-hidden": true,
            role: "img",
            focusable: "false"
          }), /*#__PURE__*/jsxs(StyledLegalese, {
            children: [/*#__PURE__*/jsxs(Text, {
              fontType: "bodyHeading",
              children: ["\xA9", new Date().getFullYear(), " Zillow Home Loans, LLC"]
            }), /*#__PURE__*/jsxs(StyledNMLS, {
              display: "flex",
              justifyContent: "space-between",
              children: [/*#__PURE__*/jsx(Text, {
                children: "An Equal Housing Lender"
              }), /*#__PURE__*/jsx(Text, {
                children: "NMLS ID#: 10287"
              }), /*#__PURE__*/jsx(StyledAnchor, {
                href: "http://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/10287",
                children: "www.nmlsconsumeraccess.org"
              })]
            }), showPassportOffer && /*#__PURE__*/jsx(StyledAdDisclaimer, {
              children: /*#__PURE__*/jsx(Text, {
                fontType: "bodySmall",
                children: passportDisclaimer
              })
            }), disclaimers.map(function (_ref5) {
              var disclaimerID = _ref5.disclaimerID,
                  markdown = _ref5.markdown;
              return /*#__PURE__*/jsx(Spacer, {
                margin: "xs",
                children: /*#__PURE__*/jsx(RenderMarkdown, {
                  markdown: markdown,
                  smallFontSizes: true
                })
              }, disclaimerID);
            })]
          })]
        })]
      }), /*#__PURE__*/jsx(SvgFooterSkyline, {
        "aria-hidden": true,
        role: "img",
        focusable: "false"
      })]
    })
  }));
};

Footer.propTypes = {
  className: PropTypes.string
};

var StyledLoadingScreen = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-1g18xjo-0"
})(["background:", ";height:calc(100vh - ", ");display:flex;justify-content:center;align-items:center;"], token('colors.backgroundLight'), token('sizing.headerHeight'));

var LoadingScreen = function LoadingScreen() {
  return /*#__PURE__*/jsx(StyledLoadingScreen, {
    children: /*#__PURE__*/jsx(Spinner, {
      size: "lg"
    })
  });
};

var isAuthenticatedSelector = authSelectors.isAuthenticatedSelector,
    userRolesSelector = authSelectors.userRolesSelector,
    hasUnauthenticatedRoleSelector = authSelectors.hasUnauthenticatedRoleSelector;
var USER_ROLES_COMMON = constants.USER_ROLES_COMMON;
/**
 * @param {Object} options - Auth options
 * @param {Boolean} options.forceAuth - Whether login should initiate automatically when the user is unauthenticated
 * @param {String[]} options.allowedRoles - An array of strings representing the user roles that are allowed access
 */

var withAuth = function withAuth(WrappedComponent, options) {
  var _ref, _WrappedComponent$dis;

  if (options === void 0) {
    options = {};
  }

  var Auth = function Auth(props) {
    var _useState = useState(),
        _useState2 = _slicedToArray(_useState, 2),
        error = _useState2[0],
        setError = _useState2[1];

    var authenticated = props.authenticated,
        userRoles = props.userRoles,
        initiateLogin = props.initiateLogin,
        acquireUnauthenticatedToken = props.acquireUnauthenticatedToken,
        hasUnauthenticatedRole = props.hasUnauthenticatedRole;
    var _options = options,
        forceAuth = _options.forceAuth,
        _options$allowedRoles = _options.allowedRoles,
        allowedRoles = _options$allowedRoles === void 0 ? [] : _options$allowedRoles;
    var isEveryRoleAllowed = allowedRoles.length === 0;
    var isUnauthenticatedRoleExplicitlyAllowed = allowedRoles.includes(USER_ROLES_COMMON.UNAUTHENTICATED_USER);
    var isUnauthenticatedRoleAllowed = isEveryRoleAllowed || isUnauthenticatedRoleExplicitlyAllowed;
    var shouldFetchUnauthenticatedToken = !authenticated && !forceAuth && isUnauthenticatedRoleAllowed;
    var shouldRedirectToLogin = forceAuth && (!authenticated || hasUnauthenticatedRole);
    useEffect(function () {
      var maybeAuthenticate = function maybeAuthenticate() {
        return new Promise(function ($return, $error) {
          var $Try_1_Post = function () {
            try {
              return $return();
            } catch ($boundEx) {
              return $error($boundEx);
            }
          };

          var $Try_1_Catch = function (err) {
            try {
              setError(err);
              return $Try_1_Post();
            } catch ($boundEx) {
              return $error($boundEx);
            }
          };

          try {
            if (shouldRedirectToLogin) {
              return Promise.resolve(initiateLogin()).then(function ($await_4) {
                try {
                  return $If_2.call(this);
                } catch ($boundEx) {
                  return $Try_1_Catch($boundEx);
                }
              }.bind(this), $Try_1_Catch);
            } else {
              if (shouldFetchUnauthenticatedToken) {
                return Promise.resolve(acquireUnauthenticatedToken()).then(function ($await_5) {
                  try {
                    return $If_3.call(this);
                  } catch ($boundEx) {
                    return $Try_1_Catch($boundEx);
                  }
                }.bind(this), $Try_1_Catch);
              }

              function $If_3() {
                return $If_2.call(this);
              }

              return $If_3.call(this);
            }

            function $If_2() {
              return $Try_1_Post();
            }
          } catch (err) {
            $Try_1_Catch(err);
          }
        });
      };

      maybeAuthenticate(); // only initiate login on mount

      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    if (error) {
      // allow our React error boundaries to handle any errors, should they occur
      // (instead of swallowing promise rejections encountered during useEffect's async calls)
      throw error;
    } // display a loading screen while the user is being redirected to login


    if (shouldRedirectToLogin) {
      return /*#__PURE__*/jsx(LoadingScreen, {});
    }

    var isRoleRestricted = !isEveryRoleAllowed;
    var matchingRoles = intersection(allowedRoles, userRoles);
    var hasInsufficientUserRoles = isRoleRestricted && matchingRoles.length === 0;

    if (authenticated && hasInsufficientUserRoles) {
      return /*#__PURE__*/jsx(Text, {
        children: "You do not have sufficient permission to proceed."
      });
    }

    return /*#__PURE__*/jsx(WrappedComponent, Object.assign({}, props));
  };

  Auth.displayName = "withAuth(" + ((_ref = (_WrappedComponent$dis = WrappedComponent.displayName) !== null && _WrappedComponent$dis !== void 0 ? _WrappedComponent$dis : WrappedComponent.name) !== null && _ref !== void 0 ? _ref : 'WrappedComponent') + ")";
  Auth.propTypes = {
    authenticated: PropTypes.bool,
    userRoles: PropTypes.arrayOf(PropTypes.string),
    hasUnauthenticatedRole: PropTypes.bool,
    initiateLogin: PropTypes.func.isRequired,
    acquireUnauthenticatedToken: PropTypes.func.isRequired
  };
  return Auth;
};

var Auth = compose(connect(function (state) {
  return {
    authenticated: isAuthenticatedSelector(state),
    userRoles: userRolesSelector(state),
    hasUnauthenticatedRole: hasUnauthenticatedRoleSelector(state)
  };
}, function (dispatch) {
  return {
    initiateLogin: function initiateLogin() {
      return dispatch(authReducers.initiateLoginThunk());
    },
    acquireUnauthenticatedToken: function acquireUnauthenticatedToken() {
      return dispatch(authReducers.acquireUnauthenticatedToken());
    }
  };
}), withAuth);

var auth = persistReducer({
  version: 1,
  key: 'auth',
  keyPrefix: 'reduxPersist:',
  storage: sessionStorage
}, authReducers.default);

var BLACKLISTED_QUERY_PARAMS = ['showBorrowerPairsManagement', 'refinanceDisclaimerState'];
var queryParams = persistReducer({
  version: 1,
  key: 'queryParams',
  keyPrefix: 'reduxPersist:',
  storage: sessionStorage,
  blacklist: BLACKLISTED_QUERY_PARAMS,
  // We want to use the reduced state as the source of truth over the session storage
  // This is to allow a new query param to override an existing
  stateReconciler: function stateReconciler(inbound, original, reduced) {
    return Object.assign({}, original, inbound, reduced);
  }
}, queryParamsReducers.default);

var longFlow = persistReducer({
  version: 1,
  key: 'longFlow',
  keyPrefix: 'reduxPersist:',
  storage: storage
}, longFlowReducers.default);

var combinedReducers = combineReducers({
  auth: auth,
  longFlow: longFlow,
  queryParams: queryParams,
  cms: cmsReducers.default
});
var store = createStore(function (state, action) {
  if (action.type === commonReducers.CLEAR_STORE) {
    return combinedReducers(undefined, action);
  } else {
    return combinedReducers(state, action);
  }
});
var persistor = persist(store);

var External = (function (Component) {
  return function (props) {
    return /*#__PURE__*/jsx(ThemeProvider, {
      theme: ThemeConstellation,
      children: /*#__PURE__*/jsx(Provider, {
        store: store,
        children: /*#__PURE__*/jsx(Component, Object.assign({}, props, {
          external: true
        }))
      })
    });
  };
});

var _constants$LOAN_TYPES = constants.LOAN_TYPES,
    PURCHASE = _constants$LOAN_TYPES.PURCHASE,
    REFINANCE = _constants$LOAN_TYPES.REFINANCE;
var StyledUpsellHeroContent = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1mksrjg-0"
})(["text-align:center;"]);
var StyledCTA = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-1mksrjg-1"
})(["margin:", " 0;", "{min-width:240px;margin:", " ", " 0 ", ";}text-align:center;"], spaceMixin('sm'), StyledButton, spaceMixin('xs'), spaceMixin('xs'), spaceMixin('xs'));

var FooterUpsell = function FooterUpsell(_ref) {
  var upsellText = _ref.upsellText,
      gaEventCategory = _ref.gaEventCategory,
      gaEventLabel = _ref.gaEventLabel,
      inverse = _ref.inverse;
  var onCTAClick = useCallback(function (loanType) {
    gaEventCategory && gaEventLabel ? trackEvent({
      category: gaEventCategory,
      action: loanType + " Button Click",
      label: gaEventLabel
    }) : null;
  }, [gaEventCategory, gaEventLabel]);
  return /*#__PURE__*/jsxs(StyledUpsellHeroContent, {
    children: [upsellText && /*#__PURE__*/jsx(Heading, {
      level: "3",
      fontFamily: "serif",
      children: upsellText
    }), /*#__PURE__*/jsxs(StyledCTA, {
      children: [/*#__PURE__*/jsx(Button, {
        as: "a",
        href: "/pre-qualify/",
        onClick: function onClick() {
          return onCTAClick(PURCHASE);
        },
        buttonType: "primary",
        inverse: inverse,
        children: "Purchase a home"
      }), /*#__PURE__*/jsx(Button, {
        as: "a",
        href: "/get-started/#/" + REFINANCE,
        onClick: function onClick() {
          return onCTAClick(REFINANCE);
        },
        buttonType: "primary",
        inverse: inverse,
        children: "Refinance my home"
      })]
    })]
  });
};

FooterUpsell.propTypes = {
  upsellText: PropTypes.string,
  gaEventCategory: PropTypes.string,
  gaEventLabel: PropTypes.string,
  inverse: PropTypes.bool
};

var _templateObject$6, _templateObject2$4, _templateObject3$3, _templateObject4$3;
var StyledHero = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1o13uea-0"
})(["display:flex;flex-direction:column;justify-content:space-evenly;background-color:", ";height:auto;margin:0 auto;text-align:center;width:100%;"], token('colors.backgroundBlue'));
var mobileIconStyles = css(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteralLoose(["\n    display: block;\n    cursor: pointer;\n    margin: 0 auto;\n    ", "\n"])), mediaMixin('lg', css(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteralLoose(["\n            display: none;\n        "])))));
var StyledIconChevronDown = /*#__PURE__*/styled(IconChevronDown).withConfig({
  componentId: "sc-1o13uea-1"
})(["", ""], mobileIconStyles);
var StyledIconChevronUp = /*#__PURE__*/styled(IconChevronUp).withConfig({
  componentId: "sc-1o13uea-2"
})(["", ""], mobileIconStyles);
var StyledHeroTextContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1o13uea-3"
})(["display:", ";flex-direction:column;max-width:960px;margin:0 auto;padding:0 ", ";", ""], function (_ref) {
  var offerOpen = _ref.offerOpen;
  return "" + (offerOpen ? 'flex' : 'none');
}, spaceMixin('xs'), mediaMixin('lg', css(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteralLoose(["\n            display: flex;\n        "])))));
var StyledDividerContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1o13uea-4"
})(["display:", ";margin:0 auto;width:25%;", ""], function (_ref2) {
  var offerOpen = _ref2.offerOpen;
  return "" + (offerOpen ? 'block' : 'none');
}, mediaMixin('lg', css(_templateObject4$3 || (_templateObject4$3 = _taggedTemplateLiteralLoose(["\n            display: block;\n        "])))));

var OfferHero = function OfferHero() {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      offerOpen = _useState2[0],
      setOfferOpen = _useState2[1];

  return /*#__PURE__*/jsxs(StyledHero, {
    children: [/*#__PURE__*/jsx(Spacer, {
      marginTop: "md",
      marginBottom: "md",
      children: /*#__PURE__*/jsx(Heading, {
        level: 2,
        fontFamily: "serif",
        children: "Passport Members Save $1,500 at Closing!"
      })
    }), /*#__PURE__*/jsx(StyledDividerContainer, {
      offerOpen: offerOpen,
      children: /*#__PURE__*/jsx(Divider, {
        appearance: "emphasized"
      })
    }), /*#__PURE__*/jsx(StyledHeroTextContainer, {
      offerOpen: offerOpen,
      children: /*#__PURE__*/jsx(Spacer, {
        marginTop: "md",
        marginBottom: "md",
        children: /*#__PURE__*/jsx(Text, {
          children: "Welcome Passport Unlimited Members! Save up to $1,500 on closing costs when you refinance or purchase your home with Zillow Home Loans today. Our partnership with Passport Unlimited helps you save on closing costs. These benefits aren\u2019t available to just anyone. Get started below!"
        })
      })
    }), /*#__PURE__*/jsx(UnstyledButton, {
      onClick: function onClick() {
        return setOfferOpen(!offerOpen);
      },
      "aria-label": offerOpen ? 'Close offer content' : 'Open offer content',
      children: offerOpen ? /*#__PURE__*/jsx(StyledIconChevronUp, {
        size: "sm",
        fontColor: "brand",
        "aria-hidden": "true"
      }) : /*#__PURE__*/jsx(StyledIconChevronDown, {
        size: "sm",
        fontColor: "brand",
        "aria-hidden": "true"
      })
    })]
  });
};

var ExternalHeader = External(Header$1);
var ExternalFooter = External(Footer);
var ExternalMobileNav = External(MobileNav);
var ExternalABAD = External(ABAD$1);
var ExternalFooterUpsell = External(FooterUpsell);

export { ABAD$1 as ABAD, ActiveNavigationLink, CurrentPathContext, ExternalABAD, ExternalFooter, ExternalFooterUpsell, ExternalHeader, ExternalMobileNav, Footer, FooterUpsell, Header$1 as Header, LoadingScreen, MobileNav, NAVIGATION_ITEMS, OfferHero, PRIMARY_NAV_SHAPE, PageContext, PersistorContext, RenderMarkdown, SUBNAV_ITEMS, SUB_NAV_ITEM_SHAPE, SUB_NAV_SHAPE, SignIn, StyledExternalSubNavLink, SvgZhlLogo as ZHLLogo, queryParams, Auth as withAuth };
